import { useState, useEffect, useRef, useCallback } from "react";

import { AgGridReact } from "ag-grid-react";
import { useTranslation, withTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { cloneDeep, isEmpty, isEqual, isNull, isUndefined, values, includes, isNil } from "lodash";
import { format, subMonths, set, addDays, addMonths, addWeeks } from "date-fns";

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import FormControl from "@mui/material/FormControl"
import FormControlLabel from "@mui/material/FormControlLabel"
import Radio from "@mui/material/Radio"
import RadioGroup from "@mui/material/RadioGroup"
import Slider from "@mui/material/Slider"
import ArrowRightIcon from "@mui/icons-material/ArrowForward";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import GavelIcon from '@mui/icons-material/Gavel';

import { Autocomplete, Checkbox, ToggleButtonGroup } from "@mui/material";
import AppCard from "@plannel/components/ui/AppCard";
import { DatePicker, LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import styled from "@emotion/styled";

import scheduleService from "@plannel/services/system/schedule-config-service";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";
import DataState from "@plannel/components/aggrid/DataState";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import GridDatePicker from "@plannel/components/GridDatePicker"
import { AddButton, RemoveButton, ResultButton, SaveButton } from "@plannel/components/ActionIconButton";
import Snackbar from "@plannel/components/Snackbar";
import Dialog from "@plannel/components/Dialog"
import reduxUtil from "@plannel/utils/redux-util";
import { useMenuAccess } from "@plannel/hooks/useMenuAccess";
import dateUtils from "@plannel/utils/date-util";
import debug from "@plannel/utils/debug-util";
import ColorUtil from "../utils/color-util";
import CodeUtil from "../utils/code-util";
import RpUtil from "../replenishment-plan/utils/rp-util";

import "@plannel/scss/components/ScheduleSetting.scss";
import { green, grey, red } from "@mui/material/colors";
import { createRoundToggleButton } from "@plannel/components/ui/RoundToggleButton";
import { PanelField, PANEL_FORM_SX, PANEL_CONTENT_PL, PANEL_CHECKBOX_SX } from "@plannel/components/ui/PanelField";
import { DpVersionSchedule } from "../demand-plan/dp-modal/DpVersionModal";
import planConfigService from "@plannel/services/data/plan-config-service";
import measureService from "@plannel/services/data/measure-service";
import hrchyConfigService from "@plannel/services/data/hrchy-config-service";
import userService from "@plannel/services/system/user-service";
import planVersionService from "@plannel/services/data/plan-version-service";
import classificationService from "@plannel/services/ip/classification-service";
import authService from "@plannel/services/system/auth-service";

import { capitalCase, snakeCase } from "change-case";
import { FS } from "@plannel/utils/font-util";

// ToggleButtonGroup 은 그룹 내부 버튼의 바깥쪽 모서리만 남기고 radius 를 0 으로 깎기 때문에
// 여기서 다시 세워준다. data-shape="pill" 규칙은 50% 규칙보다 뒤에 선언해야 한다 —
// 특정도가 같아서 순서로 이긴다.
const CustomButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  /*
   * 폭이 모자라면 다음 줄로 넘긴다.
   * 이 그룹은 최대 12칸(월)·9칸(요일)이라 한 줄로 펴면 820px 을 요구했고, 그 값이 트리거 영역의
   * minWidth 로 굳어 Full HD 노트북(브라우저가 보는 폭 1280px)에서 오른쪽 Job 카드를
   * 가로 스크롤 뒤로 밀어냈다. 접히면 세로로 조금 길어질 뿐 고를 수 있는 칸은 그대로다.
   */
  flexWrap: "wrap",
  rowGap: "4px",
  '& .MuiToggleButtonGroup-grouped': {
    marginRight: 4,
    padding: theme.spacing(0, 1),
    border: 0,
    // borderColor 는 각 버튼이 --rt-border 로 넘겨준 값을 쓴다.
    // 여기서 border 단축 속성만 쓰면 색이 currentColor 로 되돌아가 연한 테두리가 사라진다.
    "&:not(:first-of-type)": {
      border: "1px solid",
      borderColor: "var(--rt-border)",
      borderRadius: "50%"
    },
    "&:first-of-type": {
      border: "1px solid",
      borderColor: "var(--rt-border)",
      borderRadius: "50%"
    },
    // "Every" / "First Day" 는 라벨 길이가 제각각이라 50% 를 주면 타원이 된다.
    '&[data-shape="pill"]': {
      borderRadius: 999,
      padding: theme.spacing(0, 1.75),
      // 그룹이 접히기 시작하면서 이 칩만 라벨이 두 줄로 꺾여 행 높이를 밀어 올렸다.
      // 칩은 한 덩어리로 두고, 접힘은 옆의 값 토글 그룹이 맡는다.
      whiteSpace: "nowrap"
    }
  }
}));

const { snackBarMessageColor, statusColor, roundToggleColor, jobActiveColor } = ColorUtil;
const { brandTeal, weekendSun, weekendSat } = roundToggleColor;
const { module, versionStatusCode, scheduleTargetObj, rpDemandDistributionPolicy } = CodeUtil;
const { DP, RP, IP } = module;
const { NA } = versionStatusCode;
const { DATA_TRANSFORM_IP, DATA_TRANSFORM_DP_REF, INVENTORY_PLAN, REPLENISHMENT_PLAN, DEMAND_PLAN, DATA_TRANSFORM_RP_MEASURE } = scheduleTargetObj;
const { EQUAL_BY_BUCKET, LAST_OF_BUCKET } = rpDemandDistributionPolicy;
const jobByModule = {
  [DP]: [DEMAND_PLAN, DATA_TRANSFORM_DP_REF],
  [IP]: [DATA_TRANSFORM_IP, INVENTORY_PLAN],
  [RP]: [REPLENISHMENT_PLAN],
};
const EVERY_MONTH_ON = "everyMonthOn";
const EVERY_WEEK_ON = "everyWeekOn";
const EVERY_DAY_ON = "everyDayOn";
const UNIT_WEEK = "unitW";
const DEFAULT_TRIGGER_SETTING = { second: "0", minute: "0", hour: "0", day: "*", month: ["*"], week: "", dayOfWeek: ["?"], year: "" };
const DEFAULT_TITME_PARENT_STYLE = { display: "inline-flex", width: "100%", height: "5%", minHeight: 30, };
// 섹션 헤더는 꽉 찬 티일 바였다. 채운 바는 화면에서 낼 수 있는 제일 큰 목소리인데
// 한 카드에 여러 개가 쌓이면 서로 상쇄돼서 아무것도 우선순위를 못 갖는다.
// 티일 글자 + 얇은 선이면 "새 구역"이라는 말은 다 하면서 Overview 에 강조를 양보한다.
// 배경은 남긴다 — REPLENISHMENT_PLAN 헤더가 position: sticky 라 투명하면 내용이 비쳐 보인다.
// boxSizing: 이 앱은 CssBaseline 을 전역 렌더하지 않아 border-box 리셋이 없다.
// 없으면 width:100% + pl 이 부모를 10px 넘겨 가로 스크롤바를 만든다.
const DEFAULT_TITLE_STYLE = { display: "flex", alignItems: "center", width: "100%", boxSizing: "border-box", bgcolor: "background.paper", pl: "10px" };
const SECTION_RULE_COLOR = "#d7e8ec";
// 오른쪽 Job Info 폼의 폭 상한. 없으면 "90" 한 글자짜리 입력칸이 500px 넘게 늘어난다.
const PANEL_FORM_MAX_WIDTH = 640;

const SectionTitle = ({ children }) => (
  <Box sx={DEFAULT_TITLE_STYLE}>
    <Typography variant="subtitle2" sx={{ flexShrink: 0, color: "primary.main", letterSpacing: ".06em" }}>
      {children}
    </Typography>
    <Box sx={{ flex: 1, height: "1px", bgcolor: SECTION_RULE_COLOR, ml: 1.5 }} />
  </Box>
);
const ScheduleSettings = ({ t, title }) => {
  const userData = JSON.parse(localStorage.getItem("user"));

  const reduxDispatch = useDispatch();
  const { isPermitted } = useMenuAccess();
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [schedules, setSchedules] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [userRoles, setUserRoles] = useState([]);

  const gridRef = useRef(null);
  const triggerSettingRef = useRef(DEFAULT_TRIGGER_SETTING);
  const jobInfoSettingRef = useRef({});
  const selectedRowRef = useRef(null);

  const [jobList, setJobList] = useState([]);

  const valueFormatter = params => {
    return GridUtils.gridValueL10N(params.value);
  }

  const onCellValueChanged = (e) => {
    DefaultGridSetting({}).onCellValueChanged(e);

    if (e?.column?.colId === "scheduleTarget") {
      setSelectedRow(e);
    }
  }

  const statusCellStyle = (params) => {
    const style = {};
    const headerName = params?.colDef?.field;
    const value = params?.value;
    if (headerName === "status" && value) {
      style.backgroundColor = statusColor[value.toUpperCase()];
      style.fontWeight = "bold";
    }
    return style;
  }

  const showHistoryPage = () => {
    const reduxKey = "SCHEDULE_HISTORY";
    reduxDispatch(reduxUtil.updateHistoryState(reduxKey));
  }

  const columnDefs = [
    { headerName: "scheduleName", field: "name", width: 200 },
    {
      headerName: "scheduleTarget", field: "scheduleTarget",
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      cellEditorParams: {
        values: jobList
      },
      valueFormatter,
      width: 200
    },
    { headerName: "status", field: "status", valueFormatter, cellStyle: statusCellStyle, type: "nonEditableColumn", width: 150 },
    { headerName: "activeFlg", field: "activeFlg", type: ["booleanColumn"], width: 80 },
    { headerName: "startDate", field: "startDate", cellEditor: GridDatePicker, cellEditorParams: { dateType: "day" }, width: 120 },
    { headerName: "endDate", field: "expireDate", cellEditor: GridDatePicker, cellEditorParams: { dateType: "day" }, width: 120 },
    { headerName: "createdTs", field: "createdTs", valueFormatter: dateUtils.formatDateTime, type: "nonEditableColumn" },
    { headerName: "createdBy", field: "createdUserName", colId: "createdBy", type: "nonEditableColumn", width: 150 },
    { headerName: "updatedTs", field: "updatedTs", valueFormatter: dateUtils.formatDateTime, type: "nonEditableColumn" },
    { headerName: "updatedBy", field: "updatedUserName", colId: "updatedBy", type: "nonEditableColumn", width: 150 }
  ];

  const addRow = () => {
    const newRow = {
      scheduleName: "",
      scheduleTarget: isEmpty(jobList) ? null : jobList[0],
      status: NA,
      jobInfo: {},
      scheduleTrigger: DEFAULT_TRIGGER_SETTING,
      activeFlg: true,
      startDate: format(new Date(), "yyyy-MM-dd"),
      expireDate: "",
      createdTs: null,
      createdUser: null,
      updatedTs: null,
      updatedUser: null,
      verNum: 0
    };

    DataState.setDataState(GridUtils.addRow(gridRef.current.api, newRow));
  }

  const retrieveSchedules = () => {
    scheduleService.getSchedule()
      .then((response) => {
        if (response?.data && response.data?.length > 0) {
          const jobSchedule = [];
          for (const data of response.data) {
            const { scheduleTarget } = data;
            if (jobList.includes(scheduleTarget)) {
              jobSchedule.push(data);
            }
          }
          setSchedules(jobSchedule);
        } else {
          setSchedules([]);
        }
      })
      .catch(e => {
        debug(e);
      })
      .finally(() => {
        DataState.initialize(gridRef.current?.api);
        jobInfoSettingRef.current = {};
        setSelectedRow(null);
      });
  }

  const retrieveUserRoles = () => {
    userService.getUserAccessRoles({ username: userData.username })
      .then(
        (response) => {
          const userAccess = response.data;
          setUserRoles(userAccess?.roles);
        },
      )
      .catch(e => { debug(e); });
  };

  const saveSchedule = () => {
    if (selectedRow.data.scheduleTarget === INVENTORY_PLAN) {
      const isValid = jobInfoSettingRef.current?.isValid;

      if (!isValid) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_NotConfiguredClassifi") });
        return;
      }
    }

    const saveState = cloneDeep(selectedRow?.data);

    if (saveState) {
      const runningFlg = saveState.status === "running";
      saveState.scheduleTrigger = triggerSettingRef.current;
      saveState.jobInfo = jobInfoSettingRef.current;
      saveState.timeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const allStateData = [saveState];
      if (runningFlg) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_ScheduleSaveFailRunSchedule") });
      } else {
        scheduleService.upsert(allStateData)
          .then(() => {
            selectedRowRef.current = selectedRow;
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
            retrieveSchedules();
          })
          .catch(e => {
            debug(e);
            const detailErrorMsgInfo = e.response?.data?.message?.message ? { content: t(e.response?.data?.message?.message), duration: 3000 } : {};
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed"), ...detailErrorMsgInfo });
          });
      }
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  }

  const removeSchedule = () => {
    const scheduleIds = [];
    gridRef.current.api.getSelectedRows().forEach(
      (data) => {
        scheduleIds.push(data.id)
      }
    );

    if (scheduleIds.length > 0) {
      scheduleService.remove(scheduleIds)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        })
        .catch(e => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
          debug(e);
        })
        .finally(() => {
          retrieveSchedules();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSelectedData") });
    }
  }

  const handleDialog = (action) => {
    if (action === "SAVE") {
      saveSchedule()
    } else if (action === "DELETE") {
      removeSchedule();
    }
  };

  const onRowSelected = (event) => {
    const { node } = event;

    if (node.isSelected()) {
      setSelectedRow(event);
    }
  }

  useEffect(() => {
    if (!isEmpty(schedules) && !isEmpty(selectedRowRef.current)) {
      selectedRowRef.current.node.setSelected(true);
    }

  }, [schedules])

  useEffect(() => {
    if (gridRef.current?.api && !isEmpty(jobList)) {
      DataState.initialize(gridRef.current?.api);
      retrieveSchedules();
    }

    return () => {
      setSchedules([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobList]);

  useEffect(() => {
    const appRolePrefix = "ROLE_APP_";
    const availableModules = [];
    if (!isEmpty(userRoles)) {
      const appRoles = userRoles.filter(role => role.includes("APP"));
      if (appRoles) {
        for (const appRole of appRoles) {
          availableModules.push(appRole.replace(appRolePrefix, ""));
        }
      }
    }

    const list = [];
    for (const avilableModule of availableModules) {
      if (jobByModule[avilableModule]) {
        list.push(...jobByModule[avilableModule]);
      }
    }

    if (availableModules.includes(RP) && !availableModules.includes(DP)) {
      list.push(DATA_TRANSFORM_RP_MEASURE);
    }

    list.sort();
    setJobList(list);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRoles])

  useEffect(() => {
    retrieveUserRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Box id={"schedule-settings"} sx={{ width: "100%", height: "100%", mr: 1, overflow: "auto" }}>
        <Box id="schedule-setting" sx={{ height: "calc(100% - 4px)", minHeight: "550px", width: "100%" }}>
          <Box sx={{ height: "40px" }}>
            {/* 제목을 뺀 자리라 flex-end 로 바꾼다 — space-between 은 자식이 하나면
                flex-start 와 같아서 버튼이 왼쪽으로 붙는다. */}
            <Box sx={{ display: "flex", height: "35px", justifyContent: "flex-end", alignItems: "end" }}>
              <Box>
                {/* 이력 화면이 권한 밖이면 버튼을 내보내지 않는다 */}
                {isPermitted("SCHEDULE_HISTORY") && <ResultButton onClick={() => showHistoryPage()} />}
                <AddButton onClick={addRow} />
                <RemoveButton onClick={() => {
                  gridRef.current.api.stopEditing();
                  setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })
                }} />
                <SaveButton onClick={() => {
                  gridRef.current.api.stopEditing();
                  setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })
                }} />
              </Box>
            </Box>
          </Box>
          <Box sx={{ width: "100%", height: "calc(100% - 40px)", mt: 0.5 }}>
            <Box id="schedule-grid" className="ag-theme-balham" sx={{ width: "100%", height: "30%", minHeight: "200px", mb: 1 }}>
              <AgGridReact
                ref={gridRef}
                {...DefaultGridSetting({ title })}
                rowData={schedules}
                rowSelection={{ mode: 'singleRow', enableClickSelection: true }}
                columnDefs={GridUtils.getColumnDefs(columnDefs)}
                onRowSelected={onRowSelected}
                onCellValueChanged={onCellValueChanged}
              />
            </Box>
            <Box id={"schedule-detail-area"} sx={{ width: "100%", height: "calc(70% - 8px)" }}>
              <Box id={"zoom-078"} sx={{ width: "100%", height: "100%" }}>
                {selectedRow ?
                  <ScheduleDetailArea
                    selectedRow={selectedRow}
                    triggerSettingRef={triggerSettingRef}
                    jobInfoSettingRef={jobInfoSettingRef}
                    setSnackInfo={setSnackInfo}
                    userRoles={userRoles}
                  /> :
                  <Box sx={{ display: "inline-flex", width: "calc(100% - 6px)", height: "calc(100% - 8px)", alignItems: "center", justifyContent: "center", border: "3px dotted #01579b" }}>
                    <Typography variant="h6" color="#01579b" fontWeight="bold">{t("MSG_NoSelectedRow")}</Typography>
                  </Box>
                }
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

/*
  왼쪽 카드(Job 필드 + 스케줄 행)의 라벨 거터. PanelField 의 PANEL_LABEL_SX 와 같은 말투이고
  폭만 다르다 — 이쪽 라벨이 훨씬 짧다. 최장은 vi "Ngày trong Tuần"/"Cơ Sở Thời Gian"(15자 ≈ 95px).
  고정폭이라 칩 시작선이 행마다 어긋나지 않고, 왼쪽 정렬이라 짧은 라벨 앞에 빈칸이 없다.
*/
const ROW_LABEL_SX = {
  flexShrink: 0,
  alignSelf: "center",
  ml: 2,
  width: 116,
  pr: 2,
  textAlign: "left",
  fontSize: FS.SUB,
  color: "text.secondary",
};

// "Every" 는 값이 아니라 모드(전체 선택)라서 값 칩과 갈라 놓는다.
const ScheduleRowDivider = () => (
  <Divider orientation="vertical" flexItem sx={{ mx: 1.5, my: 0.5 }} />
);

// Job Target / Name / Active 같은 key-value 한 줄. 라벨은 스케줄 행과 같은 거터를 쓴다.
const JobField = ({ label, children }) => (
  <Box sx={{ display: "flex", alignItems: "center", width: "100%", minHeight: 28 }}>
    <Box sx={ROW_LABEL_SX}>{label}</Box>
    <Typography variant="body2" component="span">{children}</Typography>
  </Box>
);


const TOGGLE_DIAMETER = 36;

const MONTH_ARRAY = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const MONTH_OBJ = {
  1: "jan",
  2: "feb",
  3: "mar",
  4: "apr",
  5: "may",
  6: "jun",
  7: "jul",
  8: "aug",
  9: "sep",
  10: "oct",
  11: "nov",
  12: "dec",
}
const WEEK_ARRAY = [1, 2, 3, 4, 5];
const DAY_OF_WEEK_ARRAY = ["*", "?", "SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
const DAY_ARRAY = ["*", "?", "1"];

// String number 정렬 옵션
const getStringNumberSort = (arr, sortType) => {
  return arr.sort((a, b) => {
    const valueOfKeyA = a.toUpperCase();
    const valueOfKeyB = b.toUpperCase();

    if (sortType.toUpperCase() === "DESC") {
      return valueOfKeyB.localeCompare(valueOfKeyA, undefined, {
        numeric: true,
        sensitivity: 'base',
      });
    }

    return valueOfKeyA.localeCompare(valueOfKeyB, undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  });
};

const getDayOfweekSort = (arr, sortType) => {
  return arr.sort((a, b) => {
    if (sortType.toUpperCase() === "DESC") {
      return DAY_OF_WEEK_ARRAY.indexOf(b) - DAY_OF_WEEK_ARRAY.indexOf(a);
    }
    return DAY_OF_WEEK_ARRAY.indexOf(a) - DAY_OF_WEEK_ARRAY.indexOf(b);
  });
}

/* --------------------------------------------------------------------------  
* Job Schedule Detail Area
* Trigger, Job Info Settings Component
* @param {Object} props
* @param selectedRow - Grid selected Row Data
* -------------------------------------------------------------------------- */
const ScheduleDetailArea = (props) => {
  const { t } = useTranslation();
  const { selectedRow, triggerSettingRef, jobInfoSettingRef, setSnackInfo, userRoles } = props;
  const selectedRowData = selectedRow?.data;
  const selectedTriggerInfo = selectedRowData?.scheduleTrigger;
  const selectedPrevFireTs = selectedRowData?.prevFireTs;
  const selectedNextFireTs = selectedRowData?.nextFireTs;
  const selectedScheduleTarget = selectedRowData?.scheduleTarget;
  const selectedJobInfo = selectedRowData?.jobInfo || {};

  const [monthConfig, setMonthConfig] = useState(["*"]);
  const [weekConfig, setWeekConfig] = useState("");
  const [dayOfWeekConfig, setDayOfWeekConfig] = useState(["*"]);
  const [dayConfig, setDayConfig] = useState("1");
  const [timeConfig, setTimeConfig] = useState(new Date("1970", "01", "01", "00", "00", "00"));

  const [isUserInput, setIsUserInput] = useState(false);
  const [schedulePreview, setSchedulePreview] = useState("");
  const [renderTrigger, setRenderTrigger] = useState(0);
  const weekIsSelectedRef = useRef(false);
  const weekIsDisabledRef = useRef(false);
  const dayOfWeekDisabledRef = useRef(false);
  const dayOfWeekIsSelectedRef = useRef(false);

  // 영문 인경우 파스칼 케이스가 되어버리기에 띄어쓰기 설정을 위함.
  const localSettings = JSON.parse(localStorage.getItem("settings"));
  const activeENLang = localSettings?.lang === "en-US";
  const EVERY_TEXT = useRef(activeENLang ? `${t("every")} ` : t("every"));
  const makeScheduleOverview = (monthWord, weekWord, dayOfWeekWord, dayWord, hourWord, minutesWord) => {
    const everyMonthFlag = monthConfig.includes("*");
    const everyWeekFlag = weekConfig === "";
    const everyDayFlag = dayConfig === "*" || dayConfig === "?";
    const everyDayOfWeekFlag = dayOfWeekConfig.includes("*") || dayOfWeekConfig.includes("?");

    const getMonthWord = (mValue) => {
      let monthWordResult = mValue;

      // 매월, 매주 설정이고 요일이 설정되어있는경우 (매주 단위로 표시)
      if (everyMonthFlag && everyWeekFlag) {
        if ((!everyDayOfWeekFlag && !everyDayFlag) || everyDayFlag) {
          monthWordResult = "";
        }
      }

      return monthWordResult;
    }

    const getWeekWord = (wValue) => {
      let weekWordResult = wValue;

      // 매일 설정, 요일이 설정 안되어있는경우 (주 단위 제거)
      // 일단위가 설정되어있는경우 (주 단위 제거)
      if ((everyDayFlag && everyWeekFlag && everyDayOfWeekFlag) || !everyDayFlag) {
        weekWordResult = "";
      }

      return weekWordResult;
    }

    const getDayOfWeekWord = (dowValue) => {
      let dayOfWeekWordResult = dowValue;

      // 매주, 매일, 모든요일 인경우 (매일로 표시)
      // 일단위가 설정되어있는경우 (요일 단위 제거)
      if ((everyDayFlag && everyDayOfWeekFlag) || !everyDayFlag) {
        dayOfWeekWordResult = "";
      }

      return dayOfWeekWordResult;
    }

    const getDayWord = (dValue) => {
      let dayWordResult = dValue;

      // 주차가 설정되어있고 매일 설정인경우 (매일 제거)
      // 요일이 설정되어있는 경우 (일단위 제거 후 요일 단위 표시)
      if ((everyDayFlag && !everyWeekFlag) || !everyDayOfWeekFlag) {
        dayWordResult = "";
      }

      return dayWordResult;
    }

    monthWord = getMonthWord(monthWord);
    weekWord = getWeekWord(weekWord);
    dayOfWeekWord = getDayOfWeekWord(dayOfWeekWord);
    dayWord = getDayWord(dayWord);

    const currentSchedulePreview = `
      ${monthWord ? monthWord + t("month") : ""}
      ${(!weekIsDisabledRef.current) && weekWord ? weekWord + t("week") : ""} 
      ${(dayOfWeekWord && !dayOfWeekDisabledRef.current) ? dayOfWeekWord + t("dayOfWeek") : ""} 
      ${dayWord ? dayWord + t("day") : ""} 
      ${hourWord}:${minutesWord}
    `;

    setSchedulePreview(currentSchedulePreview);
  }

  const scheduleOverview = () => {
    let monthWord = monthConfig;
    let weekWord = "";
    let dayOfWeekWord = "";
    let dayWord = dayConfig;

    // Month Overview
    if (monthConfig.includes("*")) {
      monthWord = EVERY_TEXT.current;
    }

    // Week Overview
    if (weekConfig === "") {
      weekWord = EVERY_TEXT.current
    } else if (weekConfig !== "") {
      weekWord = weekConfig
    }

    // DayOfWeek Overview
    if (dayOfWeekConfig.includes("?")) {
      dayOfWeekWord = t(EVERY_DAY_ON);
    } else if (dayOfWeekConfig.includes("*")) {
      dayOfWeekWord = "";
    } else {
      if (activeENLang) {
        dayOfWeekWord = `${dayOfWeekConfig.map(day => t(day?.toLowerCase())).join(", ")} `;
      } else {
        dayOfWeekWord = dayOfWeekConfig.map(day => t(day?.toLowerCase())).join(", ");
      }
    }

    // Day Overview
    if (dayConfig === "?" || dayConfig === "*") {
      dayWord = EVERY_TEXT.current;
    }

    // Time Overview
    const hourWord = timeConfig?.getHours() < 10 ? `0${timeConfig?.getHours()}` : timeConfig?.getHours();
    const minutesWord = timeConfig?.getMinutes() < 10 ? `0${timeConfig?.getMinutes()}` : timeConfig?.getMinutes();

    makeScheduleOverview(monthWord, weekWord, dayOfWeekWord, dayWord, hourWord, minutesWord);
  }

  const triggerRegist = () => {
    triggerSettingRef.current = {
      ...triggerSettingRef.current,
      month: monthConfig,
      week: weekConfig,
      dayOfWeek: dayOfWeekConfig,
      day: dayConfig,
      hour: timeConfig?.getHours().toString(),
      minute: timeConfig?.getMinutes().toString(),
    }
  }

  useEffect(() => {
    if (!isEmpty(selectedTriggerInfo)) {
      setMonthConfig(selectedTriggerInfo?.month?.length === 12 ? ["*"] : selectedTriggerInfo.month);
      setWeekConfig(selectedTriggerInfo?.week);
      if (isEmpty(selectedTriggerInfo?.week)) {
        weekIsSelectedRef.current = false;
      } else {
        weekIsSelectedRef.current = true;
      }
      if (selectedTriggerInfo?.dayOfWeek?.length === 7) {
        setDayOfWeekConfig(["*"]);
        dayOfWeekIsSelectedRef.current = false;
      } else {
        setDayOfWeekConfig(selectedTriggerInfo.dayOfWeek);
        dayOfWeekIsSelectedRef.current = true;
      }

      const day = selectedTriggerInfo?.day;
      if (DAY_ARRAY.includes(day)) {
        setIsUserInput(false);
      } else {
        setIsUserInput(true);
      }
      setDayConfig(selectedTriggerInfo?.day);

      if (!isEmpty(selectedTriggerInfo?.hour) && !isEmpty(selectedTriggerInfo?.minute)) {
        setTimeConfig(new Date(1970, 0, 1, selectedTriggerInfo?.hour, selectedTriggerInfo?.minute));
      }
    } else {
      setMonthConfig(["*"]);
      setWeekConfig("");
      setDayOfWeekConfig(["?"]);
      setDayConfig("*");
      setIsUserInput(false);
      weekIsSelectedRef.current = false;
      dayOfWeekIsSelectedRef.current = false;
    }
  }, [selectedTriggerInfo])

  useEffect(() => {
    if (selectedRow) {
      if (dayConfig === "*" || dayConfig === "?") {
        weekIsDisabledRef.current = false;
        dayOfWeekDisabledRef.current = false;
      } else {
        weekIsDisabledRef.current = true;
        dayOfWeekDisabledRef.current = true;
      }

      triggerRegist();
      scheduleOverview();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthConfig, weekConfig, dayOfWeekConfig, dayConfig, timeConfig, selectedRow])

  useEffect(() => {
    setRenderTrigger(selectedRow?.rowIndex);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRow])

  const TriggerSettingArea = useCallback(() => {
    const seletedJobTarget = selectedRowData?.scheduleTarget ? t(selectedRowData?.scheduleTarget) : "None";
    const seletedJobName = selectedRowData?.name ? selectedRowData?.name : "None";
    const seletedJobActive = selectedRowData?.activeFlg ? t("activeFlg") : t("inactiveFlg");
    const activeChip = selectedRowData?.activeFlg ? jobActiveColor.active : jobActiveColor.inactive;

    return (
      <AppCard sx={{ width: "100%", height: "100%", minHeight: "300px" }}>
        <Box sx={{ width: "100%", height: "200px" }}>
          {/* Job Target / Name / Active — 아래 Month·Week 행과 같은 라벨 거터를 써서
              카드 전체가 세로 정렬선 하나에 걸리게 한다. */}
          <JobField label={t("jobTarget")}>{seletedJobTarget}</JobField>
          <JobField label={t("jobName")}>{seletedJobName}</JobField>
          <JobField label={t("jobActive")}>
            <Box component="span" sx={{
              display: "inline-flex", alignItems: "center", gap: 0.75,
              px: 1.25, py: 0.25, borderRadius: 999,
              bgcolor: activeChip.bg, color: activeChip.text, fontSize: FS.SUB
            }}>
              <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: activeChip.text }} />
              {seletedJobActive}
            </Box>
          </JobField>

          {/* Job Overview — 위아래 설정 전부를 한 문장으로 요약하는 자리라
              이 카드에서 유일하게 채워진 블록으로 둔다. */}
          <Box sx={{ mt: 1.5, mx: 2, px: 1.75, py: 1, borderRadius: 1, bgcolor: brandTeal[50] }}>
            <Typography sx={{ fontSize: FS.BODY, letterSpacing: ".06em", color: brandTeal[700] }}>
              {t("jobScheduleOverview")}
            </Typography>
            <Typography component="span" sx={{ fontSize: FS.DISPLAY, color: brandTeal[900] }}>
              {schedulePreview}
            </Typography>
          </Box>

          {/* 스케줄 설정 */}
          <Box sx={{ ...DEFAULT_TITME_PARENT_STYLE, mt: 1.5 }}>
            <SectionTitle>{t("jobSchedule")}</SectionTitle>
          </Box>
        </Box>

        <Box id={"job-schedule-detail"} sx={{ height: "calc(100% - 200px)", overflow: "auto" }}>
          <Box sx={{ width: "100%", height: "100%" }}>
            {/* 월별 설정 */}
            <Box sx={{ display: "inline-flex", width: "100%", height: "auto", mt: 1 }}>
              <MonthlySchedule
                monthConfig={monthConfig}
                setMonthConfig={setMonthConfig}
              />
            </Box>

            {/* 주별 설정 */}
            <Box sx={{ display: "inline-flex", width: "100%", height: "auto", mt: 1 }}>
              <WeeklySchedule
                weekConfig={weekConfig}
                setWeekConfig={setWeekConfig}
                weekIsSelectedRef={weekIsSelectedRef}
                weekIsDisabledRef={weekIsDisabledRef}
                dayOfWeekConfig={dayOfWeekConfig}
                setDayOfWeekConfig={setDayOfWeekConfig}
                setIsUserInput={setIsUserInput}
                dayConfig={dayConfig}
                setDayConfig={setDayConfig}
              />
            </Box>

            {/* 요일 설정 */}
            <Box sx={{ display: "inline-flex", width: "100%", height: "auto", mt: 1 }}>
              <DayOfWeekSchedule
                dayOfWeekConfig={dayOfWeekConfig}
                setDayOfWeekConfig={setDayOfWeekConfig}
                setDayConfig={setDayConfig}
                dayOfWeekIsSelectedRef={dayOfWeekIsSelectedRef}
                weekIsSelectedRef={weekIsSelectedRef}
                t={t}
              />
            </Box>

            {/* 일 설정 */}
            <Box sx={{ display: "inline-flex", width: "100%", height: "auto", mt: 1 }}>
              <DailySchedule
                dayConfig={dayConfig}
                setDayConfig={setDayConfig}
                weekConfig={weekConfig}
                dayOfWeekConfig={dayOfWeekConfig}
                setDayOfWeekConfig={setDayOfWeekConfig}
                dayOfWeekIsSelectedRef={dayOfWeekIsSelectedRef}
                setWeekConfig={setWeekConfig}
                isUserInput={isUserInput}
                setIsUserInput={setIsUserInput}
                t={t}
              />
            </Box>

            {/* 시간 설정 */}
            <Box sx={{ display: "inline-flex", width: "100%", height: "auto", mt: 1, mb: 1 }}>
              <TimeSchedule
                timeConfig={timeConfig}
                setTimeConfig={setTimeConfig}
                t={t}
              />
            </Box>
          </Box>
        </Box>
      </AppCard>
    )
  }, [selectedRowData, monthConfig, weekConfig, dayOfWeekConfig, dayConfig, timeConfig, isUserInput, t, schedulePreview]);

  return (
    <Box sx={{ width: "100%", height: "100%", minHeight: "300px", display: "inline-block" }}>
      <Box sx={{ width: "100%", height: "100%", display: "inline-flex", overflow: "auto" }}>

        {/*
          Trigger Area.
          최소폭은 "라벨 거터(148) + Every 칩(96) + 구분선(25) + 토글 6칸(236) + 카드 여백(32)" 이다.
          예전 값 820 은 월 12칸을 한 줄로 펴려던 값이라, 좁은 화면에서 이 칸이 820 에 붙박이면
          오른쪽 Job 카드가 통째로 가로 스크롤 뒤로 밀렸다(1280px 에서 228px 초과).
          이제 토글이 접히므로(CustomButtonGroup) 반으로 나눈 폭이 그대로 들어간다.
        */}
        <Box id={"trigger-area"} sx={{ width: "50%", minWidth: "540px", height: "100%", mr: 1 }}>
          <TriggerSettingArea />
        </Box>

        {/* Detail Setting Area */}
        <Box sx={{ width: "calc(50% - 8px)", height: "100%" }}>
          <JobInfoArea
            key={renderTrigger}
            prevFireTs={selectedPrevFireTs}
            nextFireTs={selectedNextFireTs}
            scheduleTarget={selectedScheduleTarget}
            jobInfo={selectedJobInfo}
            jobInfoSettingRef={jobInfoSettingRef}
            setSnackInfo={setSnackInfo}
            userRoles={userRoles}
          />
        </Box>
      </Box>
    </Box>
  )
}

/* 
  ##################################
  Month Style Button Component
  ##################################
*/
const EveryMonthButton = createRoundToggleButton(brandTeal, { diameter: TOGGLE_DIAMETER, shape: "pill", borderShade: 200 });

const MonthButton = createRoundToggleButton(brandTeal, { diameter: TOGGLE_DIAMETER, borderShade: 200 });
/* 
  ##################################
  Month Component
  ##################################
*/
const MonthlySchedule = (props) => {
  const { t } = useTranslation();
  const { monthConfig, setMonthConfig } = props;

  // month click handler
  const monthClickHandler = (month, allFlg) => {
    if (allFlg) {
      setMonthConfig([month]);
    } else {
      if (monthConfig?.includes("*")) {
        setMonthConfig([month]);
        return;
      }

      const sortBefore = monthConfig?.includes(month) ? monthConfig?.filter(m => m !== month) : [...monthConfig, month];
      if (sortBefore?.length === 0 || sortBefore?.length === 12) {
        setMonthConfig(["*"]);
        return;
      }
      setMonthConfig(getStringNumberSort(sortBefore, "ASC"));
    }
  }

  // 월별 스케줄 1 ~ 12월 버튼
  const monthlyArray = MONTH_ARRAY.map((intMonth) => {
    const month = intMonth.toString();

    const displayMonth = t(MONTH_OBJ[month]);

    return (
      <MonthButton
        key={`${month}_month_key`}
        name="month-button"
        value={month}
        onClick={() => monthClickHandler(month, false)}
      >
        {displayMonth}
      </MonthButton>
    )
  });

  return (
    <>
      <Box sx={ROW_LABEL_SX}>
        {t("month")}
      </Box>
      <CustomButtonGroup variant="outlined" aria-label="outlined button group" value={monthConfig}>
        <EveryMonthButton
          value="*"
          data-shape="pill"
          onClick={() => monthClickHandler("*", true)}
        >
          {`${t(EVERY_MONTH_ON)}`}
        </EveryMonthButton>
      </CustomButtonGroup>
      <ScheduleRowDivider />
      <CustomButtonGroup variant="outlined" aria-label="outlined button group" value={monthConfig}>
        {monthlyArray}
      </CustomButtonGroup>
    </>
  )
};


/* 
  ##################################
  Week Style Button Component
  ##################################
*/
const EveryWeekButton = createRoundToggleButton(brandTeal, { diameter: TOGGLE_DIAMETER, shape: "pill", borderShade: 200 });

const WeekButton = createRoundToggleButton(brandTeal, { diameter: TOGGLE_DIAMETER, borderShade: 200 });

/* 
  ##################################
  Week Component
  ##################################
*/
const WeeklySchedule = (props) => {
  const { t } = useTranslation();
  const { weekConfig, setWeekConfig, weekIsSelectedRef, dayOfWeekConfig, setIsUserInput, setDayOfWeekConfig, dayConfig, setDayConfig } = props;

  const weekClickHandler = (week) => {
    const selectedWeek = weekConfig === week ? "" : week;

    if (!isEmpty(selectedWeek)) {
      // Every Week 
      if (dayOfWeekConfig?.includes("?") || dayOfWeekConfig?.includes("*") || dayConfig === "*" || dayConfig === "?") {
        setDayConfig("?");
        setDayOfWeekConfig(["*"]);
      }
      setIsUserInput(false);
      weekIsSelectedRef.current = true;
    } else {
      weekIsSelectedRef.current = false;
    }
    setWeekConfig(selectedWeek);
  }

  // 주별 스케줄 1 ~ 5주 버튼
  const weekArray = WEEK_ARRAY.map((intWeek) => {
    const week = intWeek.toString();

    return (
      <WeekButton
        key={`${week}_week_key`}
        aria-label={`${week}${t(UNIT_WEEK)}`}
        value={week}
        onClick={() => weekClickHandler(week)}
      >
        {`${week}${t(UNIT_WEEK)}`}
      </WeekButton>
    )
  });

  return (
    <>
      <Box sx={ROW_LABEL_SX}>
        {t("week")}
      </Box>
      <CustomButtonGroup aria-label="outlined button group" value={weekConfig}>
        <EveryWeekButton
          value=""
          data-shape="pill"
          onClick={() => weekClickHandler("")}
        >
          {`${t(EVERY_WEEK_ON)}`}
        </EveryWeekButton>
      </CustomButtonGroup>
      <ScheduleRowDivider />
      <CustomButtonGroup aria-label="outlined button group" value={weekConfig}>
        {weekArray}
      </CustomButtonGroup>
    </>
  )
}

/* 
  ##################################
  Day of week Style Button Component
  ##################################
*/
const DayOfWeekButton = createRoundToggleButton(brandTeal, { diameter: TOGGLE_DIAMETER, borderShade: 200 });

const DayOfWeekButtonSun = createRoundToggleButton(weekendSun, { diameter: TOGGLE_DIAMETER, borderShade: 200 });

const DayOfWeekButtonSat = createRoundToggleButton(weekendSat, { diameter: TOGGLE_DIAMETER, borderShade: 200 });
/* 
  ##################################
  Day of week Schedule Component
  ##################################
*/
const DayOfWeekSchedule = (props) => {
  const { dayOfWeekConfig, setDayOfWeekConfig, setDayConfig, dayOfWeekIsSelectedRef, weekIsSelectedRef, t } = props;

  // dow click handler
  const dowClickHandler = (dow) => {
    if (dow === "*") {
      // Every Week Of Days
      dayOfWeekIsSelectedRef.current = true;
    } else {
      dayOfWeekIsSelectedRef.current = false;
    }

    if (weekIsSelectedRef.current) {
      setDayConfig("?");
      setDayOfWeekConfig([dow]);
      return;
    }

    // click event false or true
    let sortBefore = dayOfWeekConfig?.includes(dow) ? dayOfWeekConfig?.filter(m => m !== dow) : [...dayOfWeekConfig, dow];
    if (sortBefore?.includes("?") && sortBefore?.length > 1) {
      sortBefore = sortBefore.filter(m => m !== "?");
    }

    if (sortBefore?.includes("*") && sortBefore?.length > 1) {
      sortBefore = sortBefore.filter(m => m !== "*");
    }

    if (sortBefore.length === 7 || sortBefore.length === 0) {
      setDayOfWeekConfig(["?"]);
      setDayConfig("*");
      return;
    }

    setDayConfig("?");
    setDayOfWeekConfig(getDayOfweekSort(sortBefore, "ASC"));
  }

  // 요일 스케줄 [월 - 일] 버튼
  const dayOfWeekArray = DAY_OF_WEEK_ARRAY.map((dow) => {
    const displayValue = dow;
    if (dow === "?" || dow === "*") {
      return "";
    }

    if (dow === "SAT") {
      return (
        <DayOfWeekButtonSat
          key={`${dow}_dow_key`}
          value={dow}
          aria-label={t(displayValue.toLowerCase())}
          onClick={() => dowClickHandler(dow)}
        >
          {t(displayValue.toLowerCase())}
        </DayOfWeekButtonSat>
      )
    } else if (dow === "SUN") {
      return (
        <DayOfWeekButtonSun
          key={`${dow}_dow_key`}
          value={dow}
          aria-label={t(displayValue.toLowerCase())}
          onClick={() => dowClickHandler(dow)}
        >
          {t(displayValue.toLowerCase())}
        </DayOfWeekButtonSun>
      )
    }

    return (
      <DayOfWeekButton
        key={`${dow}_dow_key`}
        value={dow}
        aria-label={t(displayValue.toLowerCase())}
        onClick={() => dowClickHandler(dow)}
      >
        {t(displayValue.toLowerCase())}
      </DayOfWeekButton>
    )
  });

  useEffect(() => {
    // All Select background color flg
    if (dayOfWeekConfig?.includes("*")) {
      dayOfWeekIsSelectedRef.current = false;
    } else {
      dayOfWeekIsSelectedRef.current = true;
    }

    //주가 선택되어있는경우 요일은 하나 or 전체만 가능. (사실 연결되는 범위 까지도 지정가능 (미구현)) FIXME : 이게뭐지
    // if ((weekIsSelectedRef.current && dayOfWeekConfig?.length > 1) || dayOfWeekConfig?.includes("*")) {
    //   if (dayOfWeekConfig?.length !== 1) {
    //     setDayOfWeekConfig(["?"]);
    //     setDayConfig("*");
    //   }
    // }

    if (!isEmpty(dayOfWeekConfig) && (dayOfWeekConfig?.length === 7)) {
      setDayOfWeekConfig(["?"]);
      setDayConfig("*");
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayOfWeekConfig]);

  return (
    <>
      <Box sx={ROW_LABEL_SX}>
        {t("dayOfWeek")}
      </Box>
      <CustomButtonGroup
        size="small"
        aria-label="Days of the week"
        value={dayOfWeekConfig}
      >
        {dayOfWeekArray}
      </CustomButtonGroup>
    </>
  )
};

/* 
  ##################################
  Day of week Style Button Component
  ##################################
*/
const DayButton = createRoundToggleButton(brandTeal, { diameter: TOGGLE_DIAMETER, shape: "pill", borderShade: 200 });

const EveryDayButton = createRoundToggleButton(brandTeal, { diameter: TOGGLE_DIAMETER, shape: "pill", borderShade: 200 });
const DailySchedule = (props) => {
  const { dayConfig, setDayConfig, dayOfWeekConfig, setDayOfWeekConfig, dayOfWeekIsSelectedRef, weekConfig, setWeekConfig, isUserInput, setIsUserInput, t } = props;
  const dayGroupValue = (dayConfig === "?" && dayOfWeekConfig?.includes("*")) ? "*" : dayConfig;

  const dayClickHandler = (day) => {
    if (day !== "*" && day !== "?") {
      setWeekConfig("");
    }

    // Day 설정시 요일도 선택 할 수 없고. Day 설정시 "?" 로 Cron식이 작성되어야함.
    if (day === "*" && weekConfig !== "") {
      setDayConfig("?");
      setDayOfWeekConfig(["*"]);
    } else {
      if (day === "?") {
        setDayOfWeekConfig(["*"]);
      } else {
        setDayOfWeekConfig(["?"]);
      }

      setDayConfig(day);
    }
    setIsUserInput(false);
  }

  // "L" 말일 데이터 추가예정. (DayButton 을 하나 더 두면 된다)

  useEffect(() => {
    if ((dayOfWeekIsSelectedRef.current || dayOfWeekConfig?.includes("*")) && !dayOfWeekConfig?.includes("?")) {
      setDayConfig("?");
      setIsUserInput(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayOfWeekConfig, dayOfWeekIsSelectedRef])

  useEffect(() => {
    if (dayOfWeekConfig?.includes("?")) {
      if (dayConfig === "?") {
        setDayConfig("*");
        setIsUserInput(false);
      }
    } else if (dayOfWeekConfig?.includes("*")) {
      if (dayConfig === "*") {
        setDayConfig("?");
        setIsUserInput(false);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayConfig, dayOfWeekConfig]);

  return (
    <>
      <Box sx={ROW_LABEL_SX}>
        {t("day")}
      </Box>
      <CustomButtonGroup variant="outlined" aria-label="outlined button group" value={dayGroupValue}>
        <EveryDayButton
          value="*"
          data-shape="pill"
          onClick={() => dayClickHandler("*")}
        >
          {t(EVERY_DAY_ON)}
        </EveryDayButton>
      </CustomButtonGroup>
      <ScheduleRowDivider />
      <CustomButtonGroup variant="outlined" aria-label="outlined button group" value={dayGroupValue}>
        <DayButton
          value="1"
          data-shape="pill"
          onClick={() => dayClickHandler("1")}
        >
          {t("firstDay")}
        </DayButton>
      </CustomButtonGroup>
      {dayConfig &&
        <InputDaySchedule
          dayConfig={dayConfig}
          isUserInput={isUserInput}
          setDayConfig={setDayConfig}
          setDayOfWeekConfig={setDayOfWeekConfig}
          setIsUserInput={setIsUserInput}
          setWeekConfig={setWeekConfig}
          t={t}
        />
      }
    </>
  )
};

const TimeSchedule = (props) => {
  const { timeConfig, setTimeConfig, t } = props;

  const [timeValue, setTimeValue] = useState(timeConfig);

  const inValidDateCheck = (date) => {
    if (date instanceof Date && !isNaN(date)) {
      return date;
    } else {
      return new Date("1970", "01", "01", "00", "00", "00");
    }
  }

  const onBlurHandler = () => {
    setTimeConfig(inValidDateCheck(timeValue));
  }

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      onBlurHandler();
    }
  }

  const timeChangeHandler = (newValue) => {
    setTimeValue(newValue);
  }

  return (
    <>
      <Box sx={ROW_LABEL_SX}>
        {t("time")}
      </Box>

      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <TimePicker
          ampm={false}
          label={t("time")}
          openTo={"hours"}
          views={['hours', 'minutes']}
          format="HH:mm"
          value={timeValue}
          onChange={timeChangeHandler}
          onAccept={(e) => setTimeConfig(inValidDateCheck(e))}
          slotProps={{ textField: { onBlur: onBlurHandler, onKeyPress: handleKeyPress, size: "small", sx: { width: 120 } } }}
        />
      </LocalizationProvider>
    </>
  )
};

const InputDaySchedule = (props) => {
  const { dayConfig, setDayConfig, setDayOfWeekConfig, setWeekConfig, isUserInput, setIsUserInput, t } = props;

  const [dayInputValue, setDayInputValue] = useState(isUserInput ? dayConfig : 1);
  const [errorValue, setErrorValue] = useState(false);

  const dayInputHandler = (e) => {
    setDayInputValue(e.target.value);
  }

  const onBlurHandler = () => {
    if (!errorValue) {
      setWeekConfig("");
      setDayConfig(dayInputValue);
      setDayOfWeekConfig(["?"]);
      setIsUserInput(true);
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      onBlurHandler();
    }
  }

  useEffect(() => {
    if (isNaN(dayInputValue)) {
      setErrorValue(true);
    } else {
      if (dayInputValue < -1) {
        setDayInputValue(1);
      } else if (dayInputValue > 31) {
        setDayInputValue(31);
      }
      setErrorValue(false);
    }
  }, [dayInputValue])

  return (
    <TextField
      size="small"
      label={t("day")}
      color="info"
      sx={{ width: 50, backgroundColor: isUserInput ?? "#a7d5ee", color: isUserInput ?? "#fff" }}
      focused={isUserInput}
      error={errorValue}
      value={dayInputValue}
      onKeyPress={handleKeyPress}
      onChange={dayInputHandler}
      onBlur={onBlurHandler}
    />
  )
}

/*
* #######################################
* #######################################
* JOB Schedule Info Component
* Info is previus and next schedule time
* #######################################
* #######################################
*/
const ScheduleJobInfo = (props) => {
  const { t } = useTranslation();
  const { prevFireTs, nextFireTs } = props;

  return (
    <Box>
      <Box sx={{ ...DEFAULT_TITME_PARENT_STYLE, mt: 1 }}>
        <SectionTitle>{t("nextSchedule")}</SectionTitle>
      </Box>
      <PanelField label={t("prevJobSchedule")}>
        <Typography variant="body2" component="span">
          {prevFireTs ? dateUtils.formatDateTime(prevFireTs) : t("noPlan")}
        </Typography>
      </PanelField>
      <PanelField label={t("nextJobSchedule")} sx={{ mt: 1, mb: 1 }}>
        <Typography variant="body2" component="span">
          {nextFireTs ? dateUtils.formatDateTime(nextFireTs) : t("noPlan")}
        </Typography>
      </PanelField>
    </Box>
  )
}


/*
* ##########################
* ##########################
* JOB Info Setting Component
* ##########################
* ##########################
*/
const RANGE = "range";
const CUST_LEVEL_CD = "custLevelCd";
const ITEM_LEVEL_CD = "itemLevelCd";

// IP Transform
const PERIOD = "period";
const OPTION = "option";
const CURRENT_DATE = new Date();
const IP_TRANSFORM_END_DATE = new Date(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth() + 1, 0);
const IP_DEFAULT_PERIOD = 3;
// DP Transform
const { customer, item } = CodeUtil;
const { APP_DP } = CodeUtil.roles; 
const RESULTS = "results";
const IS_ONLY_RP = "isOnlyRp";
const SALES_PLAN = "salesPlan"
const YOY_PERIOD = 'yoyPeriod';
const FF_PERIOD = 'ffPeriod';
const SA_PERIOD = 'saPeriod';
const SO_PERIOD = 'soPeriod';
const ASA_PERIOD = 'asaPeriod';
const SALES_PLAN_PERIOD = 'salesPlanPeriod';
const SALES_PLAN_MEASURE_PERIOD = 'salesPlanMeasurePeriod';
const YOY_OPTION = 'yoyOption';
const FF_OPTION = 'ffOption';
const SA_OPTION = 'saOption';
const SO_OPTION = 'soOption';
const ASA_OPTION = 'asaOption';
const SALES_PLAN_MEASURE_OPTION = 'salesPlanMeasureOption';
const SALES_PLAN_OPTION = 'salesPlanOption';
const DP_TXN_DEFAULT_RANGE = 3;
const IS_VALID = 'isValid'
const AVG_MONTHS = "avgMonths";

// DP Plan
const MODULE_CD = "moduleCd";
const { dpConfig } = CodeUtil;
const { PLANNING_RANGE, BUCKET_UNIT, START_DAY, INITIAL_DEMAND, RUN_BF, BF_VERSION } = dpConfig;

// IP Plan
const DELETE = "DELETE";
const SALES_MONTHS = "salesMonths";
const DEMAND_MONTHS = "demandMonths";
const CLASSIFICATION_ID = "classificationId";
const SOURCE_VERSION_ID = "sourceVersionId";
const SIMULATION = "simulation";
const ACTUAL_DEFAULT_PERIOD = 6;
const DEMAND_DEFAULT_PERIOD = 0;
const IP_INITIAL_START_DATE = new Date(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth(), 1);
const IP_INITIAL_END_DATE = new Date(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth(), 0);
const { READY, RELEASED } = CodeUtil.versionStatusCode;

// RP Plan 
const BUCKET = "bucket";
const SALES_REF_START = "salesRefStart";
const SALES_REF_END = "salesRefEnd"
const DEMAND_REF_START = "demandRefStartDate";
const DEMAND_REF_END = "demandRefEndDate"
const DEMAND_DIS_POLICY = "demandDistributionPolicy";
const BACK_ORDER = "backorder";
const SALES = "SALES";
const DEMAND = "DEMAND";
const KIT_DEMAND_MERGE_FLG = 'kitDemandMergeFlg';
const NOTE_FLG = 'noteFlg';
const USE_TARGET_SUPPLY_DAYS = 'useTargetSupplyDays';
const IMMEDIATE_SHIPMENT = 'immediateShipment';

const rpDefaultValue = RpUtil.configDefaultValue;
const JobInfoArea = (props) => {
  const { prevFireTs, nextFireTs, scheduleTarget, jobInfo, jobInfoSettingRef, setSnackInfo, userRoles } = props;

  const dataTransformIPJobInfo = {
    [PERIOD]: jobInfo?.[PERIOD] || IP_DEFAULT_PERIOD,
    [OPTION]: jobInfo?.[OPTION]
  };

  const dataTransformDPRefJobInfo = {
    [YOY_PERIOD]: jobInfo?.[YOY_PERIOD] || DP_TXN_DEFAULT_RANGE,
    [FF_PERIOD]: jobInfo?.[FF_PERIOD] || DP_TXN_DEFAULT_RANGE,
    [SA_PERIOD]: jobInfo?.[SA_PERIOD] || DP_TXN_DEFAULT_RANGE,
    [SO_PERIOD]: jobInfo?.[SO_PERIOD] || DP_TXN_DEFAULT_RANGE,
    [ASA_PERIOD]: jobInfo?.[ASA_PERIOD] || DP_TXN_DEFAULT_RANGE,
    [SALES_PLAN_PERIOD]: jobInfo?.[SALES_PLAN_PERIOD] || DP_TXN_DEFAULT_RANGE,
    [SALES_PLAN_MEASURE_PERIOD]: jobInfo?.[SALES_PLAN_MEASURE_PERIOD] || DP_TXN_DEFAULT_RANGE,
    [YOY_OPTION]: jobInfo?.[YOY_OPTION],
    [FF_OPTION]: jobInfo?.[FF_OPTION],
    [SA_OPTION]: jobInfo?.[SA_OPTION],
    [SO_OPTION]: jobInfo?.[SO_OPTION],
    [ASA_OPTION]: jobInfo?.[ASA_OPTION],
    [SALES_PLAN_MEASURE_OPTION]: jobInfo?.[SALES_PLAN_MEASURE_OPTION],
    [SALES_PLAN_OPTION]: jobInfo?.[SALES_PLAN_OPTION],
    [CUST_LEVEL_CD]: jobInfo?.[CUST_LEVEL_CD] || customer.cd,
    [ITEM_LEVEL_CD]: jobInfo?.[ITEM_LEVEL_CD] || item.cd,
    [AVG_MONTHS]: jobInfo?.[AVG_MONTHS] || 3
  };

  const inventoryPlanJobInfo = {
    [SALES_MONTHS]: jobInfo?.[SALES_MONTHS] ?? ACTUAL_DEFAULT_PERIOD,
    [DEMAND_MONTHS]: jobInfo?.[DEMAND_MONTHS] ?? DEMAND_DEFAULT_PERIOD,
    [CLASSIFICATION_ID]: jobInfo?.[CLASSIFICATION_ID] || null,
    [SOURCE_VERSION_ID]: jobInfo?.[SOURCE_VERSION_ID] || null
  };

  const replenishmentPlanJobInfo = {
    [BUCKET]: jobInfo?.[BUCKET] || rpDefaultValue?.[BUCKET],
    [RANGE]: jobInfo && Object.prototype.hasOwnProperty.call(jobInfo, RANGE) ? jobInfo?.[RANGE] : rpDefaultValue?.[RANGE],
    [BACK_ORDER]: jobInfo?.[BACK_ORDER] || rpDefaultValue?.[BACK_ORDER],
    [DEMAND_DIS_POLICY]: jobInfo?.[DEMAND_DIS_POLICY] || rpDefaultValue?.[DEMAND_DIS_POLICY],
    [DEMAND_REF_START]: jobInfo?.[DEMAND_REF_START] || rpDefaultValue?.[DEMAND_REF_START],
    [DEMAND_REF_END]: jobInfo?.[DEMAND_REF_END] || rpDefaultValue?.[DEMAND_REF_END],
    [KIT_DEMAND_MERGE_FLG]: jobInfo?.[KIT_DEMAND_MERGE_FLG] || false,
    [NOTE_FLG]: jobInfo?.[NOTE_FLG] || false,
    [USE_TARGET_SUPPLY_DAYS]: jobInfo?.[USE_TARGET_SUPPLY_DAYS] || false,
    [SALES_REF_START]: jobInfo?.[SALES_REF_START] || rpDefaultValue?.[SALES_REF_START],
    [SALES_REF_END]: jobInfo?.[SALES_REF_END] || rpDefaultValue?.[SALES_REF_END],
    [IMMEDIATE_SHIPMENT]: jobInfo?.[IMMEDIATE_SHIPMENT] || rpDefaultValue?.[IMMEDIATE_SHIPMENT],
  };

  const demandPlanJobInfo = {
    [MODULE_CD]: jobInfo?.[MODULE_CD] ? jobInfo?.[MODULE_CD] : '',
    [PLANNING_RANGE]: jobInfo?.[PLANNING_RANGE] ? jobInfo?.[PLANNING_RANGE] : '',
    [BUCKET_UNIT]: jobInfo?.[BUCKET_UNIT] ? jobInfo?.[BUCKET_UNIT] : '',
    [START_DAY]: jobInfo?.[START_DAY] ? jobInfo?.[START_DAY] : '',
    [INITIAL_DEMAND]: jobInfo?.[INITIAL_DEMAND] ? jobInfo?.[INITIAL_DEMAND] : '',
    [RUN_BF]: jobInfo?.[RUN_BF] ? jobInfo?.[RUN_BF] : false
  }
  if (jobInfo?.[BF_VERSION]) {
    demandPlanJobInfo[BF_VERSION] = jobInfo?.[BF_VERSION];
  }

  const targetSetting = () => {
    switch (scheduleTarget) {
      case DATA_TRANSFORM_IP:
        return (
          <DataTransformIPSetting
            jobInfoSettingRef={jobInfoSettingRef}
            jobInfo={dataTransformIPJobInfo}
            setSnackInfo={setSnackInfo}
          />
        )
      case DATA_TRANSFORM_DP_REF:
      case DATA_TRANSFORM_RP_MEASURE:
        return (
          <DataTransformDPRefSetting
            jobInfoSettingRef={jobInfoSettingRef}
            jobInfo={dataTransformDPRefJobInfo}
            setSnackInfo={setSnackInfo}
            userRoles={userRoles}
          />
        )
      case INVENTORY_PLAN:
        return (
          <InventoryPlanSetting
            jobInfoSettingRef={jobInfoSettingRef}
            jobInfo={inventoryPlanJobInfo}
            setSnackInfo={setSnackInfo}
          />
        )
      case REPLENISHMENT_PLAN:
        return (
          <ReplenishmentPlanSetting
            jobInfoSettingRef={jobInfoSettingRef}
            jobInfo={replenishmentPlanJobInfo}
            setSnackInfo={setSnackInfo}
          />
        )
      case DEMAND_PLAN:
        return (
          <DemandPlanSetting
            jobInfoSettingRef={jobInfoSettingRef}
            jobInfo={demandPlanJobInfo}
            userRoles={userRoles}
          />
        )
      default:
        return (
          <Box>Default Page</Box>
        )
    }
  }

  return (
    <AppCard sx={{ width: "100%", height: "100%", overflow: "auto" }}>
      <Box sx={{ display: "inline-block", width: "100%", height: "100%" }}>
        <Box sx={{ height: "70%", minHeight: "300px" }}>
          {targetSetting()}
        </Box>
        <Box sx={{ height: "30%" }}>
          <ScheduleJobInfo
            prevFireTs={prevFireTs}
            nextFireTs={nextFireTs}
          />
        </Box>
      </Box>
    </AppCard>
  )
}

/*
* #######################################
* #######################################
* IP Data Transform JOB Setting Component
* #######################################
* #######################################
*/
const DataTransformIPSetting = (props) => {
  const { t } = useTranslation();
  const { jobInfoSettingRef, jobInfo, setSnackInfo } = props;
  const optionSelect = [
    { value: "deleteAndAdd", text: t("deleteAndAdd") },
    { value: "delete", text: t("delete") }
  ];

  const [periodVal, setPeriodVal] = useState(jobInfo?.[PERIOD]);
  const [option, setOption] = useState(jobInfo?.[OPTION] || optionSelect[0].value);
  const endDate = new Date(IP_TRANSFORM_END_DATE);
  const initStartDate = new Date(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth() - (periodVal - 1), 1);
  const [startDate, setStartDate] = useState(initStartDate);

  const handleBlur = () => {
    if (periodVal === "") {
      setPeriodVal(IP_DEFAULT_PERIOD);
    } 
  };

  const changePeriod = (event) => {
    const inputValue = event.target.value;

    if (inputValue === "") {
      setPeriodVal("");
      return;
    }

    // 숫자만으로 이루어진 문자열인지 확인
    if (/^\d*$/.test(inputValue)) {
      const numberValue = Number(inputValue);

      if (numberValue >= 1 && numberValue <= 120) {
        setPeriodVal(numberValue);
      } else {
        setPeriodVal("");
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_EnterIntBetween", { min: 1, max: 120 })});
      }
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_EnterIntBetween", { min: 1, max: 120 })});
    }
  };

  const onChangeOption = (event) => {
    const { target: { value } } = event;
    setOption(value);
  };


  useEffect(() => {
    jobInfoSettingRef.current = {
      ...jobInfo,
      [PERIOD]: periodVal
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setStartDate(set(subMonths(endDate, periodVal - 1), { date: 1 }));
    jobInfoSettingRef.current = {
      ...jobInfoSettingRef.current,
      [PERIOD]: periodVal,
      [OPTION]: option,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodVal, option, jobInfoSettingRef]);

  return (
    <Box sx={{ display: "inline-block", width: "100%", height: "100%" }}>
      <Box sx={{ ...DEFAULT_TITME_PARENT_STYLE }}>
        <SectionTitle>{`${t("inventoryAggMon")} ${t("aggregation")}`}</SectionTitle>
      </Box>
      {/* "무엇이 무엇을 만드는가"를 설명하는 도식일 뿐이다. 테두리를 뺐고, 40/20/40 컬럼도
          내용 크기로 바꿨다 — 가운데 20% 가 통째로 빈칸이었다. 글자는 패널 값과 같은 14px. */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1, pl: PANEL_CONTENT_PL }}>
        <Box>
          <Typography sx={{ fontSize: FS.H2, fontWeight: "bold", mb: 0.5 }}>{t("inventoryTransaction")}</Typography>
          <Typography sx={{ fontSize: FS.H2, fontWeight: "bold", mb: 0.5 }}>{t("salesTransaction")}</Typography>
          <Typography sx={{ fontSize: FS.H2, fontWeight: "bold" }}>{t("shipmentTransaction")}</Typography>
        </Box>
        <ArrowRightIcon fontSize="small" sx={{ color: "text.secondary" }} />
        <Typography sx={{ fontSize: FS.H2, fontWeight: "bold" }}>{t("inventoryAggMon")}</Typography>
      </Box>
      <PanelField label={t("aggregationPeriod")}>
        <TextField size="small" label={t("periodStart")} disabled inputProps={{ value: format(startDate, "yyyy-MM-dd") }} sx={{ mr: 2, width: 110 }} />
        <TextField size="small" label={t("periodEnd")} disabled inputProps={{ value: format(endDate, "yyyy-MM-dd") }} sx={{ width: 110 }} />
      </PanelField>
      <PanelField label={t("periodVal")}>
        {/* inputMode 는 TextField 가 아니라 안쪽 <input> 이 받는다 — 밖에 두면 루트 div 로 흘러가 React 가 경고한다. */}
        <TextField size="small" id="outlined-number" inputProps={{ inputMode: "numeric" }} label={t("month")} value={periodVal} onChange={changePeriod} onBlur={handleBlur} sx={{ width: 245 }} />
      </PanelField>
      <PanelField label={t("option")}>
        <FormControl variant="outlined" sx={{ width: 245 }} size="small">
          <Select
            id="option"
            variant="outlined"
            name="option"
            value={option}
            onChange={onChangeOption}
          >
            {optionSelect?.map((el) => (
              <MenuItem key={el.value} value={el.value}>{el.text}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </PanelField>
    </Box>
  )
};

/*
* #############################
* #############################
* DP Plan JOB Setting Component
* #############################
* #############################
*/
const DemandPlanSetting = (props) => {
  const { jobInfoSettingRef, jobInfo, userRoles } = props;
  const { t } = useTranslation();
  const [planConfigResult, setPlanConfigResult] = useState({});
  const [oriMeasureList, setOriMeasureList] = useState([]);
  const [initialTarget, setInitialTarget] = useState([]);
  const [hrchyConfig, setHrchyConfig] = useState([]);

  const initialPlanConfig = () => {
    const param = {
      moduleCd: DP,
      activeFlg: true
    };

    planConfigService.getAll(param)
      .then(response => {
        setPlanConfigResult(response.data?.results);
      })
      .catch(e => {
        debug(e);
      });
  };

  const retrieveHrchyConfig = () => {
    hrchyConfigService.getAll({ searchFilters: { hrchyNum: CodeUtil.hrchyNum.HRCHY } })
      .then(response => {
        if (response.data?.results) {
          const result = response.data.results;
          result.sort((a, b) => {
            if (a.seqNum < b.seqNum) {
              return -1;
            }

            if (a.seqNum > b.seqNum) {
              return 1;
            }
            return 0;
          });
          setHrchyConfig(result);
        }
      })
      .catch(e => {
        debug(e);
      })
  };

  const retrieveMeasures = () => {
    measureService
      .getAll({ isBasic: true })
      .then(response => {
        if (response.data?.results) {
          const result = response.data.results;
          setOriMeasureList(result);
          setInitialTarget(result.filter(item => item.measureCd.includes("SF") || item.measureCd.includes("MF")));
        }
      })
      .catch(e => {
        debug(e);
      });
  };

  useEffect(() => {
    retrieveHrchyConfig();
    initialPlanConfig();
    retrieveMeasures();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobInfo[MODULE_CD], jobInfo[PLANNING_RANGE], jobInfo[BUCKET_UNIT], jobInfo[START_DAY], jobInfo[INITIAL_DEMAND], jobInfo[RUN_BF], jobInfo[BF_VERSION]])

  return (
    // JobInfoArea 가 이 패널을 height 70% 로 묶고 그 아래 30% 에 Next Schedule 을 둔다.
    // overflowY 가 없으면 넘친 내용이 흘러나와 Next Schedule 위에 겹친다(InventoryPlanSetting 과 동일 처리).
    <Box sx={{ display: "inline-block", width: "100%", height: "100%", overflowY: "auto" }}>
      <Box sx={{ ...DEFAULT_TITME_PARENT_STYLE }}>
        <SectionTitle>{t(DEMAND_PLAN)}</SectionTitle>
      </Box>
      {/* 폭 상한은 여기서 건다 — DpVersionSchedule 은 DpVersionModal 소유라
          이 화면 사정을 그쪽에 박아넣지 않는다. */}
      <Box sx={{ width: "100%", maxWidth: PANEL_FORM_MAX_WIDTH }}>
        <DpVersionSchedule
          planConfigResult={planConfigResult}
          oriMeasureList={oriMeasureList}
          initialTarget={initialTarget}
          jobInfoSettingRef={jobInfoSettingRef}
          jobInfo={jobInfo}
          hrchyConfig={hrchyConfig}
          userRoles={userRoles}
        />
      </Box>
    </Box>
  )
}

/*
* ##############################################
* ##############################################
* DP Data Transform (Plan) JOB Setting Component
* ##############################################
* ##############################################
*/
const MEASURES = {
  SA_QTY: "actualSales",
  YOY_QTY: "salesActualYoY",
  FF_QTY: "financePlan",
  SO_QTY: "salesOrder",
  SALES_PLAN_QTY: "salesPlanMeasure",
  ASA_QTY: "avgSalesActual"
}

/*
 * 화면에 보이는 이름은 행 데이터에 담지 않고 그릴 때 만든다.
 * 번역문을 행에 굳히면 언어를 바꿔도 state 가 그대로라 새로 조회할 때(=새로고침)까지 옛 언어가 남는다.
 * 컬럼 정의는 setColumnDefs 시점의 t 를 가둬 두므로, 여기서는 호출 시점에 언어 번들을 읽는
 * gridValueL10N 을 쓴다(언어가 바뀌면 relocalizeAllGrids 가 셀을 다시 그려 준다).
 */
const targetNameFormatter = params => {
  const { targetCd, targetName } = params?.data ?? {};
  if (targetName === SALES_PLAN) {
    return GridUtils.gridValueL10N(SALES_PLAN);
  }
  return MEASURES[targetCd]
    ? `${GridUtils.gridValueL10N("measure")} - ${GridUtils.gridValueL10N(MEASURES[targetCd])}`
    : GridUtils.gridValueL10N(targetName);
};
/*
* #####################################################
* #####################################################
* DP Data Transform (Transaction) JOB Setting Component
* #####################################################
* #####################################################
*/
const DataTransformDPRefSetting = (props) => {
  const { jobInfoSettingRef, jobInfo, setSnackInfo, userRoles } = props;
  const { SCENARIO } = CodeUtil.dpVersion;
  const { t } = useTranslation();
  const buckets = CodeUtil.buckets;
  const optionSelect = [
    { value: "deleteAndAdd", text: t("deleteAndAdd") },
    { value: "delete", text: t("delete") }
  ];
  const gridRef = useRef();
  const addOption = [{ value: "add", text: t("add") }]

  const [gridTargetDatas, setGridTargetDatas] = useState([]);
  const [gridApi, setGridApi] = useState(null);
  const [itemLevelCds, setItemLevelCds] = useState([]);
  const [custLevelCds, setCustLevelCds] = useState([]);
  const [periodUom, setPeriodUom] = useState();
  const [isOnlyRp, setIsOnlyRp] = useState();
  const [dpPlanConfigResult, setDpPlanConfigResult] = useState({});
  const [yoyPeriod, setYoYPeriod] = useState(jobInfo?.[YOY_PERIOD]);
  const [ffPeriod, setFFPeriod] = useState(jobInfo?.[FF_PERIOD]);
  const [saPeriod, setSAPeriod] = useState(jobInfo?.[SA_PERIOD]);
  const [soPeriod, setSOPeriod] = useState(jobInfo?.[SO_PERIOD]);
  const [salesPlanMeasurePeriod, setSalesPlanMeasurePeriod] = useState(jobInfo?.[SALES_PLAN_MEASURE_PERIOD]);
  const [salesPlanPeriod, setSalesPlanPeriod] = useState(jobInfo?.[SALES_PLAN_PERIOD]);
  const [yoyOption, setYoYOption] = useState(jobInfo?.[YOY_OPTION] || optionSelect[0].value);
  const [ffOption, setFFOption] = useState(jobInfo?.[FF_OPTION] || optionSelect[0].value);
  const [saOption, setSAOption] = useState(jobInfo?.[SA_OPTION] || optionSelect[0].value);
  const [soOption, setSOOption] = useState(jobInfo?.[SO_OPTION] || optionSelect[0].value);
  const [asaOption, setASAOption] = useState(jobInfo?.[ASA_OPTION] || optionSelect[0].value);
  const [salesPlanOption, setSalesPlanOption] = useState(jobInfo?.[SALES_PLAN_OPTION] || optionSelect[0].value);
  const [salesPlanMeasureOption, setSalesPlanMeasureOption] = useState(jobInfo?.[SALES_PLAN_MEASURE_OPTION]?.option || optionSelect[0].value);
  const [salesPlanCustLevel, setSalesPlanCustLevel] = useState(jobInfo?.[SALES_PLAN_MEASURE_OPTION]?.[CUST_LEVEL_CD] || customer.cd);
  const [salesPlanItemLevel, setSalesPlanItemLevel] = useState(jobInfo?.[SALES_PLAN_MEASURE_OPTION]?.[ITEM_LEVEL_CD] || item.cd);
  const [planVersionList, setPlanVersionList] = useState([]);
  const [avgMonths, setAvgMonths] = useState(jobInfo?.[AVG_MONTHS] || 3);
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const firstDayOfNextMonth = new Date(year, month + 1, 1);

  /* 값은 개월 수(숫자)만 들고 보이는 글자는 그릴 때 붙인다 — 번역문을 셀 값으로 두면
     언어를 바꿨을 때 목록에 없는 값이 되어 빈칸으로 보인다. */
  const avgMonthOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

  const jobInfoCustLevel = jobInfo?.[CUST_LEVEL_CD];
  const jobInfoItemLevel = jobInfo?.[ITEM_LEVEL_CD];

  const initialDpPlanConfig = () => {
    planConfigService.getDpPlanConfig()
      .then(response => {
        const result = response.data?.data;
        setDpPlanConfigResult(result);
      })
      .catch(e => {
        debug(e);
      });
  };

  const initialIsOnlyRp = () => {
    const appRolePrefix = "ROLE_APP_";
    const availableModules = [];
    if (!isEmpty(userRoles)) {
      const appRoles = userRoles.filter(role => role.includes("APP"));
      if (appRoles) {
        for (const appRole of appRoles) {
          availableModules.push(appRole.replace(appRolePrefix, ""));
        }
        const onlyRp = availableModules.includes(RP) && !availableModules.includes(DP);
        setIsOnlyRp(onlyRp)
      }
    }
  }

  const dateFormatter = (params) => {
    if (periodUom === buckets.MONTH) {
      return dateUtils.formatDate(params?.value, 'yyyy-MM');
    } else {
      return dateUtils.formatDate(params?.value, "yyyy-MM-dd");
    }
  }

  const itemConfigValueCellRenderer = (params) => {
    if (params) {
      const formatter = itemLevelCds.find(obj => {
        return obj.cd === params.value
      });
      if (!formatter) {
        return "";
      }
      return formatter.name;
    }
    return "";
  }

  const custConfigValueCellRenderer = (params) => {
    if (params) {
      const formatter = custLevelCds.find(obj => {
        return obj.cd === params.value
      });
      if (!formatter) {
        return "";
      }
      return formatter.name;
    }
    return "";
  }

  const setEditableFlg = (e) => {
    if (e.column?.colId === "periodUom") {
      return e.data.targetCd === CodeUtil.dpMeasure.ASA_QTY.cd;
    } else {
      return e.data.targetCd === CodeUtil.dpMeasure.FF_QTY.cd || e.data.targetCd === SALES_PLAN || e.data.targetCd === CodeUtil.dpMeasure.SALES_PLAN_QTY.cd;
    }
  }

  const optionValueCellRenderer = (params) => {
    if (params) {
      let formatter = "";
      if (params?.data?.targetCd === CodeUtil.dpMeasure.ASA_QTY.cd) {
        formatter = planVersionList.find(obj => {
          return obj.name === params.value
        });
      } else {
        let select = [...optionSelect]
        if (params?.data?.targetCd === CodeUtil.dpMeasure.FF_QTY.cd || params?.data?.targetCd === SALES_PLAN) {
          select = [...optionSelect, ...addOption]
        }
        formatter = select.find(obj => {
          return obj.value === params.value
        });
      }
      if (!formatter) {
        return "";
      }
      return formatter.text;
    }
    return "";
  }

  const optionValue = (params) => {
    if (params?.data?.targetCd === CodeUtil.dpMeasure.ASA_QTY.cd) {
      return planVersionList.map(obj => obj.name)
    } else {
      if (params?.data?.targetCd === CodeUtil.dpMeasure.FF_QTY.cd || params?.data?.targetCd === SALES_PLAN) {
        const select = [...optionSelect, ...addOption]
        return select.map(obj => obj.value)
      }
      return optionSelect.map(obj => obj.value)
    }
  }

  const periodValueCellRenderer = (params) => {
    if (params) {
      if (params?.data?.targetCd === CodeUtil.dpMeasure.ASA_QTY.cd) {
        return isNil(params.value) ? "" : `${params.value}${GridUtils.gridValueL10N("months")}`;
      }
      /* 버킷 코드(month/week/…)는 셀 값 그대로 두고 여기서만 번역한다. */
      return GridUtils.gridValueL10N(params.data?.periodUom);
    }
    return "";
  }

  const periodValue = (params) => {
    if (params?.data?.targetCd === CodeUtil.dpMeasure.ASA_QTY.cd) {
      return avgMonthOptions;
    }
    return [];
  }

  const periodCellStyle = (params) => {
    if (params.data.targetCd === CodeUtil.dpMeasure.ASA_QTY.cd) {
      return { backgroundColor: '#f5f5f5', color: '#999' };
    }
  }

  const periodEditable = (params) => {
    return params.data.targetCd !== CodeUtil.dpMeasure.ASA_QTY.cd;
  }

  const columnDefs = [
    { headerName: "targetName", field: "targetName", width: 200, type: ["nonEditableColumn"], valueFormatter: targetNameFormatter },
    { headerName: "periodUom", field: "periodUom", width: 200,
      cellEditor: "agRichSelectCellEditor",
      editable: setEditableFlg,
      valueFormatter: periodValueCellRenderer,
      cellEditorParams: {
        valueFormatter: periodValueCellRenderer,
        values: periodValue
      }
    },
    { headerName: "periodStart", field: "startDate", width: 130, valueFormatter: dateFormatter, type: ["nonEditableColumn"], cellStyle: { backgroundColor: '#f5f5f5', color: '#999' } },
    { headerName: "periodEnd", field: "endDate", width: 130, valueFormatter: dateFormatter, type: ["nonEditableColumn"], cellStyle: { backgroundColor: '#f5f5f5', color: '#999' } },
    {
      headerName: "period", field: "period", width: 80,
      valueParser: params => {
        const parsedValue = parseInt(params.newValue, 10);
        if (isNaN(parsedValue) || parsedValue <= 0) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_EnterIntBetween", { min: 1, max: 120 }) });
          return params.oldValue;
        } else {
          return parsedValue;
        }
      },
      valueGetter: params => {
        return params.data.targetCd !== CodeUtil.dpMeasure.ASA_QTY.cd ? params.data.period || 3 : "";
      },
      cellEditor: "agTextCellEditor",
      cellStyle: periodCellStyle,
      editable: periodEditable
    },
    {
      headerName: "option", field: "option", width: 150,
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      valueFormatter: optionValueCellRenderer,
      cellEditorParams: {
        valueFormatter: optionValueCellRenderer,
        values: optionValue
      }
    },
    {
      headerName: "custLevel", field: "custLevel", width: 150,
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      editable: setEditableFlg,
      valueFormatter: custConfigValueCellRenderer,
      cellEditorParams: {
        valueFormatter: custConfigValueCellRenderer,
        values: custLevelCds.map(obj => obj.cd)
      },
      cellStyle: params => ({
        backgroundColor: params.data.targetCd === CodeUtil.dpMeasure.FF_QTY.cd || params.data.targetCd === SALES_PLAN || params.data.targetCd === CodeUtil.dpMeasure.SALES_PLAN_QTY.cd ? '#fff' : '#f5f5f5'
      })
    },
    {
      headerName: "itemLevel", field: "itemLevel", width: 150,
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      editable: setEditableFlg,
      valueFormatter: itemConfigValueCellRenderer,
      cellEditorParams: {
        valueFormatter: itemConfigValueCellRenderer,
        values: itemLevelCds.map(obj => obj.cd)
      },
      cellStyle: params => ({
        backgroundColor: params.data.targetCd === CodeUtil.dpMeasure.FF_QTY.cd || params.data.targetCd === SALES_PLAN || params.data.targetCd === CodeUtil.dpMeasure.SALES_PLAN_QTY.cd ? '#fff' : '#f5f5f5'
      })
    }
  ];

  const onGridReady = (params) => {
    setGridApi(params.api);
  };

  const retrieveDpTxnTrasnformTargetData = () => {
    scheduleService.getDpTransformTargetData(DATA_TRANSFORM_DP_REF, isOnlyRp)
      .then((response) => {
        const { data } = response;

        const results = data?.[RESULTS];
        for (const result of results) {
          setPeriodUom(result.periodUom);
          /* targetName 은 서버가 준 값 그대로 둔다 — 보이는 글자는 targetNameFormatter 가 만든다. */
          const isFinance = result?.targetCd === CodeUtil.dpMeasure.FF_QTY.cd;
          const isYoY = result?.targetCd === CodeUtil.dpMeasure.YOY_QTY.cd;
          const isActualSales = result?.targetCd === CodeUtil.dpMeasure.SA_QTY.cd;
          const isSalesOrder = result?.targetCd === CodeUtil.dpMeasure.SO_QTY.cd;
          const isSalesPlan = result?.targetName === SALES_PLAN;
          const isAsa = result?.targetCd === CodeUtil.dpMeasure.ASA_QTY.cd;
          const isSalesMeasure = result?.targetCd === CodeUtil.dpMeasure.SALES_PLAN_QTY.cd;
          if (isFinance) {
            if (result.periodUom === buckets.MONTH) {
              result.startDate = new Date(now.setDate(1));
              result.endDate = new Date(addMonths(firstDayOfNextMonth, ffPeriod - 1) - 1);
            } else {
              result.startDate = dateUtils.nearDay(new Date(), result.startDay, result.periodUom);
              result.endDate = dateUtils.calculateRangeDate(result.startDate, result.periodUom, result.startDay, ffPeriod, true);
            }
            result.period = ffPeriod;
            result.option = ffOption;
            result.custLevel = jobInfoCustLevel;
            result.itemLevel = jobInfoItemLevel;
          } else if (isSalesMeasure) {
            if (result.periodUom === buckets.MONTH) {
              result.startDate = new Date(now.setDate(1));
              result.endDate = new Date(addMonths(firstDayOfNextMonth, salesPlanMeasurePeriod - 1) - 1);
            } else {
              result.startDate = dateUtils.nearDay(new Date(), result.startDay, result.periodUom);
              result.endDate = dateUtils.calculateRangeDate(result.startDate, result.periodUom, result.startDay, salesPlanMeasurePeriod, true);
            }
            result.period = salesPlanMeasurePeriod;
            result.option = salesPlanMeasureOption;
            result.custLevel = salesPlanCustLevel;
            result.itemLevel = salesPlanItemLevel;
          } else if (isSalesPlan) {
            if (result.periodUom === buckets.MONTH) {
              result.startDate = new Date(now.setDate(1));
              result.endDate = new Date(addMonths(firstDayOfNextMonth, salesPlanPeriod - 1) - 1);
            } else {
              result.startDate = dateUtils.nearDay(new Date(), result.startDay, result.periodUom);
              result.endDate = dateUtils.calculateRangeDate(result.startDate, result.periodUom, result.startDay, salesPlanPeriod, true);
            }
            result.period = salesPlanPeriod;
            result.option = salesPlanOption;
            result.custLevel = jobInfoCustLevel;
            result.itemLevel = jobInfoItemLevel;
          } else if (isYoY) {
            if (result.periodUom === buckets.MONTH) {
              result.startDate = new Date(now.setDate(1));
              result.endDate = new Date(addMonths(firstDayOfNextMonth, yoyPeriod - 1) - 1);
            } else {
              result.startDate = dateUtils.nearDay(new Date(), result.startDay, result.periodUom);
              result.endDate = dateUtils.calculateRangeDate(result.startDate, result.periodUom, result.startDay, yoyPeriod, true);
            }
            result.period = yoyPeriod;
            result.option = yoyOption;
          } else if (isActualSales) {
            if (result.periodUom === buckets.MONTH) {
              result.endDate = new Date(firstDayOfNextMonth - 1);
              result.startDate = new Date(set(subMonths(now, saPeriod - 1), { date: 1 }));
            } else {
              result.endDate = dateUtils.calculateRangeDate(dateUtils.nearDay(new Date(), result.startDay, result.periodUom), result.periodUom, result.startDay, 1, true);
              result.startDate = dateUtils.calculateRangeDate(dateUtils.nearDay(new Date(), dpPlanConfigResult?.startDay, result.periodUom), result.periodUom, result.startDay, -saPeriod + 1, false);
            }
            result.period = saPeriod;
            result.option = saOption;
          } else if (isSalesOrder) {
            if (result.periodUom === buckets.MONTH) {
              result.startDate = new Date(now.setDate(1));
              result.endDate = new Date(addMonths(firstDayOfNextMonth, soPeriod - 1) - 1);
            } else {
              result.startDate = dateUtils.nearDay(new Date(), result.startDay, result.periodUom);
              result.endDate = dateUtils.calculateRangeDate(result.startDate, result.periodUom, result.startDay, soPeriod, true);
            }
            result.period = soPeriod;
            result.option = soOption;
          } else if (isAsa) {
            const option = jobInfo?.[ASA_OPTION] ?? planVersionList?.[0]?.name;
            result.option = option;
            /* 평균 개월 수는 숫자로 들고 화면에서만 "3개월"로 그린다(periodValueCellRenderer). */
            result.periodUom = avgMonths;
            result.period = "";
            setASAOption(option);
          }
        }
        setGridTargetDatas(results);
      })
      .catch((error) => {
        debug(error);
      })
  }

  const retrievePlanVersion = () => {
    planVersionService.getAll({ moduleCd: CodeUtil.module.DP, statusCds: [SCENARIO], orderByColumn: "createdTs", sortType: "desc"})
      .then(response => {
        const { results } = response.data;
        if (isEmpty(results)) {
          return;
        }
        setPlanVersionList(results)
        GridUtils.setColumnDefs({ ...gridRef.current, columnDefs});
      })
      .catch(e => {
        debug(e);
      })
      .finally(() => {
        initialIsOnlyRp();
      })
  };


  const retrieveHrchyConfig = () => {
    hrchyConfigService.getAll({ searchFilters: { hrchyNum: CodeUtil.hrchyNum.HRCHY } })
      .then(response => {
        const itemConfig = [];
        const custConfig = [];
        if (response.data?.results) {
          const result = response.data.results;
          result.sort((a, b) => {
            if (a.seqNum < b.seqNum) {
              return -1;
            }

            if (a.seqNum > b.seqNum) {
              return 1;
            }
            return 0;
          });
          for (const row of result) {
            if (row.itemHrchyFlg) {
              itemConfig.push(row);
            } else {
              custConfig.push(row);
            }
          }
          setItemLevelCds([...itemConfig, item]);
          if (!includes(userRoles, APP_DP)) {
            setCustLevelCds([customer]);
          } else {
            setCustLevelCds([...custConfig, customer]);
          }
        }
      })
      .catch(e => {
        debug(e);
      });
  };

  const onCellValueChanged = (e) => {
    if (gridApi) {
      const editValue = e?.value;
      const editColId = e?.column?.colId;
      const rowNode = e?.node;
      /* 어느 대상인지는 코드로만 가른다 — 번역된 이름끼리 견주면 언어를 바꾸는 순간
         어느 분기에도 걸리지 않아 편집이 조용히 무시된다(조회 쪽 분기와 같은 기준). */
      const targetCd = rowNode.data.targetCd;
      const isFinance = targetCd === CodeUtil.dpMeasure.FF_QTY.cd;
      const isYoY = targetCd === CodeUtil.dpMeasure.YOY_QTY.cd;
      const isActualSales = targetCd === CodeUtil.dpMeasure.SA_QTY.cd;
      const isSalesOrder = targetCd === CodeUtil.dpMeasure.SO_QTY.cd;
      const isSalesPlan = rowNode.data.targetName === SALES_PLAN;
      const isAsa = targetCd === CodeUtil.dpMeasure.ASA_QTY.cd;
      const isSalesMeasure = targetCd === CodeUtil.dpMeasure.SALES_PLAN_QTY.cd;
      if (editColId === "period") {
        const startDate = rowNode.data.startDate;
        const period = parseInt(editValue, 10);

        if (startDate && !isNaN(period)) {
          if (isFinance || isYoY || isSalesPlan || isSalesOrder || isSalesMeasure) {
            const newEndDate = dateUtils.calculateRangeDate(startDate, periodUom, dpPlanConfigResult?.startDay, period, true);
            rowNode.setDataValue("endDate", newEndDate);
            gridApi.flashCells({
              rowNodes: [rowNode],
              columns: ["endDate"],
              flashDelay: 500,
              fadeDelay: 1000,
            });

            if (isFinance) {
              setFFPeriod(period);
            } else if (isSalesPlan) {
              setSalesPlanPeriod(period);
            } else if (isYoY) {
              setYoYPeriod(period);
            } else if (isSalesOrder) {
              setSOPeriod (period);
            } else if (isSalesMeasure) {
              setSalesPlanMeasurePeriod(period);
            }
          } else if (isActualSales) {
            const newStartDate = new Date(dateUtils.calculateRangeDate(dateUtils.nearDay(new Date(), dpPlanConfigResult?.startDay, periodUom), periodUom, dpPlanConfigResult?.startDay, -period + 1, false));
            rowNode.setDataValue("startDate", newStartDate);
            setSAPeriod(period);
            gridApi.flashCells({
              rowNodes: [rowNode],
              columns: ["startDate"],
              flashDelay: 500,
              fadeDelay: 1000,
            });
          }
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_EnterIntBetween", { min: 1, max: 120 })});
        }
      } else if (editColId === "option") {
        if (isFinance) {
          setFFOption(editValue);
        } else if (isSalesPlan) {
          setSalesPlanOption(editValue);
        } else if (isYoY) {
          setYoYOption(editValue);
        } else if (isActualSales) {
          setSAOption(editValue);
        } else if (isSalesOrder) {
          setSOOption(editValue);
        } else if (isAsa) {
          setASAOption(editValue);
        } else if (isSalesMeasure) {
          const options = salesPlanMeasureOption;
          options.option = editValue;
          setSalesPlanMeasureOption(options);
        }
      } else if (editColId === "custLevel") {
        if (isSalesMeasure) {
          setSalesPlanCustLevel(editValue);
        } else {
          jobInfoSettingRef.current = {
            ...jobInfoSettingRef.current,
            [CUST_LEVEL_CD]: editValue,
          };
        }
      } else if (editColId === "itemLevel") {
        if (isSalesMeasure) {
          setSalesPlanItemLevel(editValue);
        } else {
          jobInfoSettingRef.current = {
            ...jobInfoSettingRef.current,
            [ITEM_LEVEL_CD]: editValue,
          };
        }

      } else if (editColId === "periodUom") {
        if (isAsa) {
          /* 셀 값이 개월 수(숫자) 자체다 — 예전처럼 "3개월" 에서 숫자를 뽑아내지 않는다. */
          const n = parseInt(editValue, 10);
          setAvgMonths(isNaN(n) ? 3 : n);
        }
      }
    }
  };

  useEffect(() => {
    jobInfoSettingRef.current = {
      ...jobInfoSettingRef.current
    };
    initialDpPlanConfig();
    retrieveHrchyConfig();
    retrievePlanVersion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isNil(isOnlyRp)) {
      retrieveDpTxnTrasnformTargetData();
    }

    jobInfoSettingRef.current = {
      ...jobInfoSettingRef.current,
      [IS_ONLY_RP]: isOnlyRp,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnlyRp]);

  useEffect(() => {
    jobInfoSettingRef.current = {
      ...jobInfoSettingRef.current,
      [CUST_LEVEL_CD]: jobInfo?.[CUST_LEVEL_CD],
      [ITEM_LEVEL_CD]: jobInfo?.[ITEM_LEVEL_CD]
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridTargetDatas]);

  useEffect(() => {
    jobInfoSettingRef.current = {
      ...jobInfoSettingRef.current,
      [YOY_PERIOD]: yoyPeriod,
      [FF_PERIOD]: ffPeriod,
      [SALES_PLAN_PERIOD]: salesPlanPeriod,
      [SA_PERIOD]: saPeriod,
      [SO_PERIOD]: soPeriod,
      [SALES_PLAN_MEASURE_PERIOD]: salesPlanMeasurePeriod
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saPeriod, salesPlanPeriod, ffPeriod, yoyPeriod, soPeriod, salesPlanMeasurePeriod])

  useEffect(() => {
    const options = {};
    options.option = salesPlanMeasureOption;
    options[ITEM_LEVEL_CD] = salesPlanItemLevel;
    options[CUST_LEVEL_CD] = salesPlanCustLevel;

    jobInfoSettingRef.current = {
      ...jobInfoSettingRef.current,
      [YOY_OPTION]: yoyOption,
      [FF_OPTION]: ffOption,
      [SALES_PLAN_OPTION]: salesPlanOption,
      [SA_OPTION]: saOption,
      [SO_OPTION]: soOption,
      [ASA_OPTION]: asaOption,
      [SALES_PLAN_MEASURE_OPTION]: options,
      [AVG_MONTHS]: avgMonths
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saOption, salesPlanOption, ffOption, yoyOption, soOption, asaOption, salesPlanMeasureOption, avgMonths, salesPlanItemLevel, salesPlanCustLevel])


  return (
    <Box sx={{ display: "inline-block", width: "100%", height: "100%" }}>
      <Box sx={{ ...DEFAULT_TITME_PARENT_STYLE }}>
        <SectionTitle>{isOnlyRp ? `${t(DATA_TRANSFORM_RP_MEASURE)}` : `${t(DATA_TRANSFORM_DP_REF)}`}</SectionTitle>
      </Box>
      <Box id="schedule-grid" className="ag-theme-balham" sx={{ overflow: "auto", width: "100%", height: "50%" }}>
        <AgGridReact
          ref={gridRef}
          rowData={gridTargetDatas}
          columnDefs={GridUtils.getColumnDefs(columnDefs)}
          {...DefaultGridSetting({})}
          defaultColDef={{
            minWidth: 30,
            editable: true,
            resizable: true
          }}
          onGridReady={onGridReady}
          onCellValueChanged={onCellValueChanged}
        />
      </Box>
    </Box>
  )
};

/*
* #############################
* #############################
* IP Plan JOB Setting Component
* #############################
* #############################
*/
// const HtmlTooltip = styled(({ className, ...styleProps }) => (
//   <Tooltip {...styleProps} classes={{ popper: className }} />
// ))(({ theme }) => ({
//   [`& .${tooltipClasses.tooltip}`]: {
//     backgroundColor: '#f5f5f9',
//     color: 'rgba(0, 0, 0, 0.87)',
//     maxWidth: 230,
//     fontSize: theme.typography.pxToRem(12),
//     border: '1px solid #dadde9',
//   },
// }));
const InventoryPlanSetting = (props) => {
  const { jobInfoSettingRef, jobInfo, setSnackInfo } = props;

  const { t } = useTranslation();
  const [actualSalesMonths, setActualSalesMonths] = useState(jobInfo?.[SALES_MONTHS]);
  const [demandPlanMonths, setDemandPlanMonths] = useState(jobInfo?.[DEMAND_MONTHS]);
  const classificationId = jobInfo?.[CLASSIFICATION_ID];
  const [classificationList, setClassificationList] = useState([]);
  const [classification, setClassification] = useState(null);
  const masterData = { id: null, name: t("masterData"), descTxt: "" };
  const sourceVersionId = jobInfo?.[SOURCE_VERSION_ID]
  const [sourceVersion, setSourceVersion] = useState(null);
  const [sourceData, setSourceData] = useState([]);
  const [planVersions, setPlanVersions] = useState([]);
  const startDate = useRef(new Date(IP_INITIAL_END_DATE.getFullYear(), IP_INITIAL_END_DATE.getMonth() - (jobInfo?.[SALES_MONTHS] - 1), 1));
  const endDate = useRef(new Date(IP_INITIAL_START_DATE));

  const INV_PLC_MS = {
    PLC_TP: capitalCase("POLICY_TYPE"),
    SVC_LVL: capitalCase("TARGET_QTY"),
    INV_DAYS: capitalCase("INVENTORY_DAYS")
  };

  const defaultPolicy = {
    [`a_${snakeCase(INV_PLC_MS.SVC_LVL)}`]: null,
  }

  const convertBigIntToString = (obj) => {
    return JSON.parse(JSON.stringify(obj, (_, value) =>
      typeof value === "bigint" ? value.toString() : value
    ));
  };

  const changePeriod = (event) => {
    const inputValue = event.target.value;
    const fieldId = event.target.id;

    if (inputValue === "") {
      if (fieldId === "demand-period") {
        setDemandPlanMonths("");
      } else {
        setActualSalesMonths("");
      }
      return;
    }

    // 숫자만으로 이루어진 문자열인지 확인
    if (/^\d*$/.test(inputValue)) {
      const numberValue = Number(inputValue);

      if (numberValue === 0) {
        if (fieldId === "demand-period") {
          endDate.current = new Date(IP_INITIAL_START_DATE);
          setDemandPlanMonths(numberValue);
        } else {
          startDate.current = new Date(IP_INITIAL_END_DATE);
          setActualSalesMonths(numberValue);
        }
      } else if (numberValue >= 1 && numberValue <= 120) {
        if (fieldId === "demand-period") {
          endDate.current = new Date(IP_INITIAL_START_DATE.getFullYear(), IP_INITIAL_START_DATE.getMonth() + numberValue, 0);
          setDemandPlanMonths(numberValue);
        } else {
          startDate.current = new Date(IP_INITIAL_END_DATE.getFullYear(), IP_INITIAL_END_DATE.getMonth() - numberValue + 1, 1);
          setActualSalesMonths(numberValue);
        }
      } else {
        if (fieldId === "demand-period") {
          setDemandPlanMonths("");
        } else {
          setActualSalesMonths("");
        }
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_EnterIntBetween", { min: 1, max: 120 })});
      }
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_EnterIntBetween", { min: 1, max: 120 })});
    }
  };

  const handleBlur = (event) => {
    const fieldId = event.target.id;

    if (fieldId === "demand-period" && demandPlanMonths === "") {
      setDemandPlanMonths(0);
      endDate.current = new Date(IP_INITIAL_START_DATE)
    } else if (fieldId === "actaul-period" && actualSalesMonths === "") {
      setActualSalesMonths(6);
      startDate.current = new Date(IP_INITIAL_END_DATE.getFullYear(), IP_INITIAL_END_DATE.getMonth() - 5, 1)
    }
  };

  const scenarioBase = {
    id: null,
    name: "",
    owner: authService.getCurrentUser().username,
    createdBy: null,
    statusCd: "NA",
    descTxt: "",
    policyObj: defaultPolicy,
    verNum: 0,
  }

  const setDefaultScenario = () => {
    const defaultPolicyType = "norDist";

    const policyHigh = {
      [`ax_${snakeCase(INV_PLC_MS.PLC_TP)}`]: defaultPolicyType,
      [`ay_${snakeCase(INV_PLC_MS.PLC_TP)}`]: defaultPolicyType,
      [`az_${snakeCase(INV_PLC_MS.PLC_TP)}`]: defaultPolicyType,
      [`bx_${snakeCase(INV_PLC_MS.PLC_TP)}`]: defaultPolicyType,
      [`by_${snakeCase(INV_PLC_MS.PLC_TP)}`]: defaultPolicyType,
      [`bz_${snakeCase(INV_PLC_MS.PLC_TP)}`]: defaultPolicyType,
      [`cx_${snakeCase(INV_PLC_MS.PLC_TP)}`]: defaultPolicyType,
      [`cy_${snakeCase(INV_PLC_MS.PLC_TP)}`]: defaultPolicyType,
      [`cz_${snakeCase(INV_PLC_MS.PLC_TP)}`]: defaultPolicyType,
      [`ax_${snakeCase(INV_PLC_MS.SVC_LVL)}`]: 97,
      [`ay_${snakeCase(INV_PLC_MS.SVC_LVL)}`]: 95,
      [`az_${snakeCase(INV_PLC_MS.SVC_LVL)}`]: 93,
      [`bx_${snakeCase(INV_PLC_MS.SVC_LVL)}`]: 91,
      [`by_${snakeCase(INV_PLC_MS.SVC_LVL)}`]: 90,
      [`bz_${snakeCase(INV_PLC_MS.SVC_LVL)}`]: 89,
      [`cx_${snakeCase(INV_PLC_MS.SVC_LVL)}`]: 85,
      [`cy_${snakeCase(INV_PLC_MS.SVC_LVL)}`]: 80,
      [`cz_${snakeCase(INV_PLC_MS.SVC_LVL)}`]: 70,
      "AX": [defaultPolicyType, 97, true, true, true, false],
      "AY": [defaultPolicyType, 95, true, true, true, false],
      "AZ": [defaultPolicyType, 93, true, true, true, false],
      "BX": [defaultPolicyType, 91, true, true, true, false],
      "BY": [defaultPolicyType, 90, true, true, true, false],
      "BZ": [defaultPolicyType, 89, true, true, true, false],
      "CX": [defaultPolicyType, 85, true, true, true, false],
      "CY": [defaultPolicyType, 80, true, true, true, false],
      "CZ": [defaultPolicyType, 70, false, true, true, false]
    }

    const serviceLevelHigh = [policyHigh[`ax_${snakeCase(INV_PLC_MS.SVC_LVL)}`], policyHigh[`ay_${snakeCase(INV_PLC_MS.SVC_LVL)}`], policyHigh[`az_${snakeCase(INV_PLC_MS.SVC_LVL)}`],
    policyHigh[`bx_${snakeCase(INV_PLC_MS.SVC_LVL)}`], policyHigh[`by_${snakeCase(INV_PLC_MS.SVC_LVL)}`], policyHigh[`bz_${snakeCase(INV_PLC_MS.SVC_LVL)}`],
    policyHigh[`cx_${snakeCase(INV_PLC_MS.SVC_LVL)}`], policyHigh[`cy_${snakeCase(INV_PLC_MS.SVC_LVL)}`], policyHigh[`cz_${snakeCase(INV_PLC_MS.SVC_LVL)}`]];

    const scenarioDescTxt = `AX to CZ (${serviceLevelHigh[0]}, ${serviceLevelHigh[1]}, ${serviceLevelHigh[2]}, ${serviceLevelHigh[3]}, ${serviceLevelHigh[4]}, ${serviceLevelHigh[5]}, ${serviceLevelHigh[6]}, ${serviceLevelHigh[7]}, ${serviceLevelHigh[8]})`

    const defaultServiceLevel = {
      ...scenarioBase,
      policyObj: policyHigh,
      newRow: true,
      name: "",
      descTxt: scenarioDescTxt
    };

    jobInfoSettingRef.current = {
      ...jobInfo,
      [SIMULATION]: defaultServiceLevel
    }
  }

  const sortPlanVersions = versions => {
    const basicVersions = [];
    const executedVersions = [];
    const releaseVersions = [];

    for (const ver of versions) {
      const { statusCd, execTs } = ver;

      if (isEqual(statusCd, RELEASED)) {
        releaseVersions.push(ver);
      } else if (!isEqual(statusCd, DELETE)) {
        if (isNull(execTs)) {
          basicVersions.push(ver);
        } else {
          executedVersions.push(ver);
        }
      }
    }

    return [...basicVersions, ...executedVersions, ...releaseVersions];
  }

  const retrieveIpVersions = () => {
    planVersionService.getAll({ moduleCd: IP, orderByColumn: "updatedTs", sortType: "desc" })
      .then(response => {
        const formattedVersions = response.data.results.map((version) =>
          convertBigIntToString(version)
        );

        setPlanVersions(sortPlanVersions(formattedVersions));

        const foundVersion = formattedVersions.find((v) => v?.id === sourceVersionId);
        setSourceVersion(foundVersion ?? masterData);
      })
      .catch(e => {
        debug(e);
      });
  };

  const retrieveClassificationList = () => {
    classificationService.getAll({ statusCd: READY })
      .then(response => {
        const { results } = response.data;
        if (isEmpty(results)) {
          setClassificationList([]);
        } else {
          setClassificationList(results);
          const foundClassification = results.find((c) => c.id === classificationId)
          setClassification(foundClassification ?? null);
        }
      })
      .catch(e => {
        debug(e);
      })
  }

  const iconColor = planVersion => {
    if (planVersion) {
      if (isEqual(planVersion.statusCd, RELEASED)) {
        return grey[900];
      }
      return isEmpty(planVersion.execTs) ? red[600] : green[600];
    }
    return null;
  }

  const validateClassification = () => {
    if (classification !== null) {
      jobInfoSettingRef.current = {
        ...jobInfoSettingRef.current,
        [IS_VALID]: true,
      };
    } else {
      jobInfoSettingRef.current = {
        ...jobInfoSettingRef.current,
        [IS_VALID]: false,
      };
    }
  }

  const onChangeClassification = obj => {
    if (obj !== null) {
      const findClassification = classificationList.find((item) => item.name === obj.name);
      setClassification(findClassification)
    } else {
      setClassification(null);
    }
  }

  const onChangeSourceData = (newValue) => {
    setSourceVersion(newValue ? convertBigIntToString(newValue) : masterData);
  };

  useEffect(() => {
    retrieveIpVersions();
    retrieveClassificationList();
    setDefaultScenario();
    validateClassification();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])



  useEffect(() => {
    jobInfoSettingRef.current = {
      ...jobInfoSettingRef.current,
      [SALES_MONTHS]: !isNaN(Number(actualSalesMonths)) ? Number(actualSalesMonths) : null,
      [CLASSIFICATION_ID]: classification?.id,
      [SOURCE_VERSION_ID]: sourceVersion?.id,
      [DEMAND_MONTHS]: !isNaN(Number(demandPlanMonths)) ? Number(demandPlanMonths) : null,
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualSalesMonths, demandPlanMonths, classification, sourceVersion]);


  useEffect(() => {
    validateClassification();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classification])

  useEffect(() => {
    setSourceData([masterData, ...planVersions]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planVersions])

  return (
    <Box sx={{ display: "inline-block", width: "100%", height: "100%", overflowY: "auto" }}>
      <Box sx={{ ...DEFAULT_TITME_PARENT_STYLE }}>
        <SectionTitle>{`${t(INVENTORY_PLAN)}`}</SectionTitle>
      </Box>
      {/* 입력칸 라벨 t("sourceData")="Source Data" 를 뺐다 — 거터의 t("sourceName")="Source Data Name"
          과 같은 말이라 한 줄에 두 번 나왔다. */}
      <PanelField label={t("sourceName")}>
        <FormControl size="small" sx={{ width: 350 }} key={"source"}>
          <Autocomplete
            key={"source"}
            options={sourceData}
            getOptionLabel={(option) => option.name}
            value={sourceVersion}
            defaultValue={null}
            onChange={(_, newValue) => onChangeSourceData(newValue)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            size="small"
            disableClearable
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Box component={isEqual(option.statusCd, RELEASED) ? GavelIcon : ViewInArIcon}
                  sx={{ width: 17, height: 17, mr: 1, color: iconColor(option) }}
                  style={{ visibility: !isNull(option.id) ? 'visible' : 'hidden' }} />
                <Box>{option.name}</Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField {...params} inputProps={{ ...params.inputProps, "aria-label": t("sourceName") }} />
            )}
          />
        </FormControl>
      </PanelField>
      {/* 거터에 t("abcXyzClassification")="ABC-XYZ Classification" 을 쓴다 — 원래 거터였던
          t("classificationName")="Classification Name" 과 동의어인데 이쪽이 더 구체적이다. */}
      <PanelField label={t("abcXyzClassification")}>
        <Box sx={{ width: 350 }}>
          <Autocomplete
            id={"abc-xyz-classification-list"}
            options={classificationList}
            getOptionLabel={(option) => option.name ?? ""}
            value={classification}
            defaultValue={null}
            onChange={(_, newValue) => onChangeClassification(newValue)}
            isOptionEqualToValue={(option, value) => {
              return option.name === value.name;
            }}
            size="small"
            sx={{ minWidth: "100%" }}
            renderInput={(params) => (
              <TextField {...params} inputProps={{ ...params.inputProps, "aria-label": t("abcXyzClassification") }} />
            )}
          />
        </Box>
      </PanelField>
      <PanelField label={`${t("actualSales")} ${t("period")}`}>
        <TextField id="actaul-period" size="small" inputProps={{ inputMode: "numeric" }} label={t("month")} value={actualSalesMonths} onChange={changePeriod} onBlur={handleBlur} sx={{ width: 110 }} />
      </PanelField>
      <PanelField label={`${t("actualSales")} ${t("overview")}`}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <TextField size="small" disabled label={t("periodStart")} inputProps={{ value: format(startDate.current, "yyyy-MM-dd") }} sx={{ mr: 2, width: 110 }} />
          <TextField size="small" disabled label={t("periodEnd")} inputProps={{ value: format(IP_INITIAL_END_DATE, "yyyy-MM-dd") }} sx={{ width: 110 }} />
        </LocalizationProvider>
      </PanelField>
      <PanelField label={`${t("demandPlan")} ${t("period")}`}>
        <TextField id="demand-period" size="small" inputProps={{ inputMode: "numeric" }} label={t("month")} value={demandPlanMonths} onChange={changePeriod} onBlur={handleBlur} sx={{ width: 110 }} />
      </PanelField>
      <PanelField label={`${t("demandPlan")} ${t("overview")}`}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <TextField size="small" disabled label={t("periodStart")} inputProps={{ value: format(IP_INITIAL_START_DATE, "yyyy-MM-dd") }} sx={{ mr: 2, width: 110 }} />
          <TextField size="small" disabled label={t("periodEnd")} inputProps={{ value: format(endDate.current, "yyyy-MM-dd") }} sx={{ width: 110 }} />
        </LocalizationProvider>
      </PanelField>
    </Box>
  )
};

/*
* #############################
* #############################
* RP Plan JOB Setting Component
* #############################
* #############################
*/
const ReplenishmentPlanSetting = (props) => {
  const { jobInfoSettingRef, jobInfo, setSnackInfo } = props;

  const { t } = useTranslation();

  const { DAY, WEEK, MONTH } = CodeUtil.buckets;
  const dayOfWeek = CodeUtil.dayOfWeek;
  const allowBackorder = [{ name: "allow", code: "true" }, { name: "notAllow", code: "false" }];
  const allowImmediateShipment = [{ name: "shipmentSame", code: "true" }, { name: "shipmentNext", code: "false" }];
  const [bucketUnit, setBucketUnit] = useState(jobInfo?.[BUCKET]);
  const [planningRange, setPlanningRange] = useState(jobInfo?.[RANGE]);
  const [salesPeriodRef, setSalesPeriodRef] = useState([jobInfo?.[SALES_REF_START], jobInfo?.[SALES_REF_END]]);
  const [demandPeriodRef, setDemandPeriodRef] = useState([jobInfo?.[DEMAND_REF_START], jobInfo?.[DEMAND_REF_END]]);
  // 초기값은 반드시 null 이어야 한다. undefined 로 두면 아래 DatePicker 두 개가 uncontrolled 로
  // 시작했다가, 마운트 직후 setInitialPeriod() 가 날짜를 채우는 순간 controlled 로 바뀌어
  // MUI 가 콘솔 에러를 낸다(null 은 "controlled 이면서 비어 있음"이라 전환이 없다).
  const [planStartDate, setPlanStartDate] = useState(null);
  const [planEndDate, setPlanEndDate] = useState(null);
  const [planStartWeek, setPlanStartWeek] = useState('');
  const [planEndWeek, setPlanEndWeek] = useState('');
  const [demandDistributionPolicy, setDemandDistributionPolicy] = useState(jobInfo?.[DEMAND_DIS_POLICY]);
  const [kitDemandMergeFlg, setKitDemandMergeFlg] = useState(jobInfo?.[KIT_DEMAND_MERGE_FLG]);
  const [noteFlg, setNoteFlg] = useState(jobInfo?.[NOTE_FLG] ?? false);
  const [useTargetSupplyDays, setUseTargetSupplyDays] =  useState(jobInfo?.[USE_TARGET_SUPPLY_DAYS] ?? false);
  const [backorder, setBackorder] = useState(jobInfo?.[BACK_ORDER]);
  const [immediateShipment, setImmediateShipment] = useState(jobInfo?.[IMMEDIATE_SHIPMENT]);
  const isMonthBucket = isEqual(bucketUnit, MONTH);
  const isWeekBucket = isEqual(bucketUnit, WEEK);
  const startDay = dayOfWeek.MONDAY.cd;

  const sliderMarks = [
    { value: -12, label: '-12' }, { value: -9, label: '-9' }, { value: -6, label: '-6' }, { value: -3, label: '-3' }, { value: 0, label: '0' },
    { value: 3, label: '3' }, { value: 6, label: '6' }, { value: 9, label: '9' }, { value: 12, label: '12' }
  ];

  const getEndDateByUom = (startDate, uom, value) => {
    let endDate = planEndDate;
    if (isEqual(uom, DAY)) {
      endDate = addDays(startDate, value);
    } else if (isEqual(uom, WEEK)) {
      endDate = addWeeks(startDate, value);
    } else if (isEqual(uom, MONTH)) {
      endDate = addMonths(startDate, value);
    }

    return addDays(endDate, -1);
  }

  const handleBlur = () => {
    let newRange = planningRange
    if (planningRange === "") {
      if (isEqual(bucketUnit, DAY)) {
        newRange = 90;
      } else if (isEqual(bucketUnit, WEEK)) {
        newRange = 12;
      } else if (isEqual(bucketUnit, MONTH)) {
        newRange = 3;
      }
    }
    setPlanningRange(newRange);

    const endDate = getEndDateByUom(planStartDate, bucketUnit, newRange);
    setPlanEndDate(endDate);
  }

  const ableDayOfWeek = e => {
    if (isWeekBucket) {
      return e.getDay() !== 1;
    }

    return false;
  };

  const getWeekStartDate = (curDate) => {
    const weekDay = values(dayOfWeek).filter(week => isEqual(week.cd, startDay))[0];
    const day = curDate.getDay();

    const convertFlg = ableDayOfWeek(curDate);
    const computeDays = Number(weekDay.id);

    if (convertFlg) {
      const convertDate = Number(curDate.getDate() - day + computeDays);
      const monthAllDays = new Date(curDate.getFullYear(), curDate.getMonth() + 1, 0).getDate();
      if (convertDate > monthAllDays) {
        return new Date(curDate.getFullYear(), curDate.getMonth() + 1, convertDate - monthAllDays);
      }
      return new Date(curDate.setDate(convertDate));
    }

    return curDate;
  };

  const onChangePlanningRange = (e) => {
    const name = e.target.name;
    const value = e.target.value;

    if (isEqual(name, "planningRange")) {
      if (isNull(value) || isUndefined(value) || isNaN(value) || isEqual(value, "") || value < 0) {
        setPlanningRange("");
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_EnterIntGreaterThanValue", { N: 1 }) });
      } else {
        const rangeVal = Number(value);
        setPlanningRange(rangeVal);
        const endDate = getEndDateByUom(planStartDate, bucketUnit, rangeVal);
        setPlanEndDate(endDate);
      }
    }

    if (isEqual(name, "bucketUnit")) {
      let newBucket = bucketUnit;
      let newRange = planningRange;
      let start = new Date();
      if (isEqual(value, DAY)) {
        newRange = 90;
        newBucket = DAY;
      } else if (isEqual(value, WEEK)) {
        start = getWeekStartDate(start);
        newRange = 12;
        newBucket = WEEK;
      } else if (isEqual(value, MONTH)) {
        start = new Date().setDate(1);
        newRange = 3;
        newBucket = MONTH;
      }

      setPlanStartDate(start);
      setBucketUnit(newBucket);
      setPlanningRange(newRange);

      const endDate = getEndDateByUom(start, value, newRange);
      setPlanEndDate(endDate);
    }
  }

  const onChangeBucketUnit = (e) => {
    const value = e.target.value;
    setBucketUnit(value);

    onChangePlanningRange({ target: { name: "bucketUnit", value } });
  }

  const handleCheckbox = (e, name) => {
    const value = e.target.checked;
    if (name === "noteFlg") {
      setNoteFlg(value);
    } else if (name === "kitSalesDemandMergeFlg") {
      setKitDemandMergeFlg(value);
    } else if (name === "useTargetSupplyDays") {
      setUseTargetSupplyDays(value);
    }
  }

  const onChangeBackorder = (event) => {
    const { target: { value } } = event;
    setBackorder(value);
  };

  const onChangeImmediateShipment = (event) => {
    const { target: { value } } = event;
    setImmediateShipment(value);
  };

  const onChangeDistributionPolicy = (e) => {
    const value = e.target.value;
    setDemandDistributionPolicy(value);
  }

  const onChangeSliderValueHandler = (_, newValue, type) => {
    if (isEqual(type, SALES)) {
      setSalesPeriodRef([newValue[0], newValue[1]]);
    } else if (isEqual(type, DEMAND)) {
      setDemandPeriodRef([newValue[0], newValue[1]]);
    }
  };

  const setInitialPeriod = () => {
    let startDate = new Date();
    if (isMonthBucket) {
      startDate = startDate.setDate(1);
      setPlanStartDate(startDate);
    } else if (isWeekBucket) {
      startDate = getWeekStartDate(startDate);
      setPlanStartDate(startDate);
    } else {
      setPlanStartDate(startDate);
    }

    const endDate = getEndDateByUom(startDate, bucketUnit, planningRange);
    setPlanEndDate(endDate);
  }

  const getWeekOfYear = (date) => {
    const currentDate = new Date(date);
    const startDate = new Date(currentDate.getFullYear(), 0, 1);
    const days = Math.floor((currentDate - startDate) /
      (24 * 60 * 60 * 1000));

    return Math.ceil(days / 7) + 1;
  }

  useEffect(() => {
    const weekNum = getWeekOfYear(planStartDate);
    const planStartWeekLabel = weekNum < 10 ? `W0${weekNum}` : `W${weekNum}`;

    setPlanStartWeek(planStartWeekLabel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planStartDate]);

  useEffect(() => {
    const weekNum = getWeekOfYear(planEndDate);
    const planEndWeekLabel = weekNum < 10 ? `W0${weekNum}` : `W${weekNum}`;

    setPlanEndWeek(planEndWeekLabel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planEndDate]);

  useEffect(() => {
    jobInfoSettingRef.current = {
      [BUCKET]: bucketUnit,
      [RANGE]: planningRange ? planningRange : 1,
      [DEMAND_DIS_POLICY]: demandDistributionPolicy,
      [SALES_REF_START]: salesPeriodRef[0],
      [SALES_REF_END]: salesPeriodRef[1],
      [DEMAND_REF_START]: demandPeriodRef[0],
      [DEMAND_REF_END]: demandPeriodRef[1],
      [KIT_DEMAND_MERGE_FLG]: kitDemandMergeFlg,
      [NOTE_FLG]: noteFlg,
      [USE_TARGET_SUPPLY_DAYS]: useTargetSupplyDays,
      [BACK_ORDER]: backorder,
      [IMMEDIATE_SHIPMENT]: immediateShipment
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bucketUnit, planningRange, demandDistributionPolicy, salesPeriodRef, demandPeriodRef, kitDemandMergeFlg, noteFlg, useTargetSupplyDays, backorder, immediateShipment])

  useEffect(() => {
    setInitialPeriod();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Box sx={{ display: "inline-block", width: "100%", height: "100%", overflowY: "hidden" }}>
      <Box sx={{ ...DEFAULT_TITME_PARENT_STYLE, position: "sticky", top: 0, zIndex: 10 }}>
        <SectionTitle>{t(REPLENISHMENT_PLAN)}</SectionTitle>
      </Box>
      <Box sx={{ width: "100%", height: "calc(100% - 50px)", overflowY: "auto", overflowX: "hidden", mt: 1 }}>
        {/* "Configuration" 라벨을 걷어냈다. 짝이 되는 값이 없는 혼자짜리 섹션 라벨이라
            바로 위 REPLENISHMENT_PLAN 헤더와 말이 겹치면서 폼만 220px 밀어내고 폭을 50%로 조였다. */}
        <Box sx={{ width: "100%" }}>
          <Box sx={{ width: "100%", maxWidth: PANEL_FORM_MAX_WIDTH, ...PANEL_FORM_SX }}>
            {/* fieldset 을 전부 걷어내고 거터 라벨로 옮겼다 — legend 가 하던 일을 거터가 한다.
                컨트롤이 라벨을 다는 건 거터가 못 하는 말이 있을 때뿐이다(Start/End, 단위). */}
            <PanelField label={t("planningBucket")}>
              <FormControl size="small" key={"bucket"}>
                <RadioGroup
                  row
                  name="row-radio-buttons-group"
                  value={bucketUnit}
                  onChange={onChangeBucketUnit}>
                  <FormControlLabel value={DAY} control={<Radio size="small" sx={{ padding: "5px" }} />} label={t("daily")} />
                  <FormControlLabel value={WEEK} control={<Radio size="small" sx={{ padding: "5px" }} />} label={t("week")} />
                  <FormControlLabel value={MONTH} control={<Radio size="small" sx={{ padding: "5px" }} />} label={t("monthly")} />
                </RadioGroup>
              </FormControl>
            </PanelField>
            {/* Plan Range 와 Period UOM 은 "90 Day" 로 읽히는 한 쌍이라 한 행에 둔다.
                Period UOM 은 읽기 전용이라 거터 없이도 단위임이 자명하지만, 라벨을 없애면
                이름이 사라지므로 outline 라벨로 남긴다(Plan Horizon 의 Start/End 와 같은 처리). */}
            <PanelField label={t("planRange")}>
              <TextField
                name="planningRange"
                sx={{ width: 110, mr: 2 }}
                value={planningRange}
                onChange={onChangePlanningRange}
                onBlur={handleBlur}
                size="small"
                type="number"
                error={isEqual(planningRange, "") || !isFinite(planningRange) || planningRange < 0}
                InputProps={{ inputProps: { min: 1, "aria-label": t("planRange") }, inputMode: 'numeric', type: 'number' }}
                variant="outlined" />
              <TextField
                name="periodUom"
                label={t("periodUom")}
                sx={{ width: 110 }}
                value={t(bucketUnit)}
                InputProps={{ readOnly: true }}
                size="small"
                variant="outlined" />
            </PanelField>
            <PanelField label={t("planHorizon")}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  disabled
                  label={t("startDate")}
                  views={isMonthBucket ? ['year', 'month'] : ['year', 'month', 'day']}
                  format={isMonthBucket ? 'yyyy-MM' : isWeekBucket ? `yyyy-MM ${planStartWeek}` : 'yyyy-MM-dd'}
                  value={planStartDate}
                  slotProps={{ textField: { size: "small", sx: { width: 150 } } }}
                />
                <Box sx={{ display: "flex", alignItems: "center", px: 1 }}>
                  <ArrowRightIcon fontSize="small" />
                </Box>
                <DatePicker
                  disabled
                  label={t("endDate")}
                  views={isMonthBucket ? ['year', 'month'] : ['year', 'month', 'day']}
                  format={isMonthBucket ? 'yyyy-MM' : isWeekBucket ? `yyyy-MM ${planEndWeek}` : 'yyyy-MM-dd'}
                  value={planEndDate}
                  slotProps={{ textField: { size: "small", sx: { width: 150 } } }}
                />
              </LocalizationProvider>
            </PanelField>
            <PanelField label={t("demandDistributionPolicy")}>
              <FormControl size="small" key={"policy"}>
                <RadioGroup
                  row
                  name="row-radio-buttons-group"
                  value={demandDistributionPolicy}
                  onChange={onChangeDistributionPolicy}>
                  <FormControlLabel value={EQUAL_BY_BUCKET} control={<Radio size="small" sx={{ padding: "5px" }} />} label={t("equalByBucket")} />
                  <FormControlLabel value={LAST_OF_BUCKET} control={<Radio size="small" sx={{ padding: "5px" }} />} label={t("lastOfBucket")} />
                </RadioGroup>
              </FormControl>
            </PanelField>
            <PanelField label={`${t("dp")} ${t("version")}`}>
              <Typography variant="body2" component="span">{t("recentlyReleasedDpVersion")}</Typography>
            </PanelField>
            <PanelField label={t("demandRefPeriodForAvgSales")}>
              <Box sx={{ display: "flex", flex: 1 }}>
                  <Slider
                    value={salesPeriodRef}
                    onChange={(_, newValue) => onChangeSliderValueHandler(_, newValue, SALES)}
                    valueLabelDisplay="auto"
                    step={1}
                    marks={sliderMarks}
                    min={-12}
                    max={0}
                    sx={{ mr: 2 }}
                  />
                  <Slider
                    value={demandPeriodRef}
                    onChange={(_, newValue) => onChangeSliderValueHandler(_, newValue, DEMAND)}
                    valueLabelDisplay="auto"
                    step={1}
                    marks={sliderMarks}
                    min={0}
                    max={12}
                    sx={{ ml: 2 }}
                  />
              </Box>
            </PanelField>
            <Box sx={{ mt: 1 }}>
              <FormControlLabel
                control={<Checkbox color="primary" name={"kitSalesDemandMergeFlg"} checked={kitDemandMergeFlg}  onChange={(param) => handleCheckbox(param, "kitSalesDemandMergeFlg")}/>}
                label={t("kitSalesDemandMergeFlg")} key="KIT_DEMAND_MERGED_FLG" sx={{ ...PANEL_CHECKBOX_SX, display: "flex" }}
              />
            </Box>
            <Box sx={{ flex: 4.5, display: "flex", justifyContent: "flex-start" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    name="useTargetSupplyDays"
                    checked={useTargetSupplyDays}
                    onChange={(param) => handleCheckbox(param, "useTargetSupplyDays")}
                  />
                }
                label={t("useTargetSupplyDays")}
                sx={{ ...PANEL_CHECKBOX_SX, display: "flex" }}
                key="USE_TARGET_SUPPLY_DAYS"
              />
              <FormControlLabel
                control={<Checkbox color="primary" name={"noteFLg"} checked={noteFlg} onChange={param => handleCheckbox(param, "noteFlg")} />}
                label={t("lastReleasedNote")} key="NOTE_FLG" sx={{ ...PANEL_CHECKBOX_SX, display: "flex" }}
              />
            </Box>
            <PanelField label={t("backorder")}>
              <FormControl variant="standard" sx={{ width: 200 }} size="small">
                <Select
                  variant="outlined"
                  id="backorder"
                  name="backorder"
                  value={backorder ?? ""}
                  onChange={onChangeBackorder}
                >
                  {allowBackorder?.map((el) => (
                    <MenuItem key={el.code} value={el.name}>{t(el.name)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </PanelField>
            <PanelField label={t("shipmentTime")}>
              <FormControl variant="standard" sx={{ width: 200 }} size="small">
                <Select
                  variant="outlined"
                  id="immediateShipment"
                  name="immediateShipment"
                  value={immediateShipment ?? ""}
                  onChange={onChangeImmediateShipment }
                >
                  {allowImmediateShipment?.map((el) => (
                    <MenuItem key={el.code} value={el.name}>{t(el.name)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </PanelField>
          </Box>
        </Box>
      </Box>
    </Box>
  )
};

export default withTranslation()(ScheduleSettings);